import type { SkillArtifactOrGroup } from './types/skill-artifact';
import type {
  Loop,
  LoopDetail,
  CreateLoopPayload,
  UpdateLoopPayload,
  PreviewLoopPayload,
} from './types/loops';

/**
 * F-FINAL-83 (2026-05-27) — Base URL configuravel via env var.
 *
 * Em prod (raios.app), `VITE_API_BASE_URL` aponta direto pro Tailscale funnel
 * (`https://mac-mini-de-breno.tail27d27e.ts.net`), bypassando a rewrite do
 * Vercel `/api/* -> tailscale` que tem hard limit ~30-60s e gera 502 quando o
 * turn de wizard demora >60s (LLM longo). Frontend agora chama backend Hono
 * direto; CORS no backend ja permite `https://raios.app` + `www.raios.app`
 * (validado em src/platform/middleware/cors.ts).
 *
 * Fallback `/api` preserva dev local (vite proxy `/api -> localhost:3000` em
 * vite.config.ts) e mantem compat caso a env var nao esteja setada (cai na
 * rewrite legada do vercel.json).
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
  error?: {
    code: string;
    message: string;
    request_id: string;
  };
}

export type HarnessSubsystem =
  | 'database'
  | 'engine'
  | 'llm'
  | 'companions'
  | 'agents'
  | 'skills'
  | 'squads'
  | 'loops'
  | 'storage'
  | 'connectors'
  | 'scheduler'
  | 'tools';

export interface HarnessHealthDay {
  day: string;
  status: 'operational' | 'degraded' | 'outage' | 'unknown';
  total: number;
  successes: number;
  failures: number;
  fallbacks: number;
  success_rate: number | null;
}

export interface HarnessCurrentHealth {
  subsystem: HarnessSubsystem;
  status: HarnessHealthDay['status'];
  total: number;
  successes: number;
  failures: number;
  fallbacks: number;
  success_rate: number | null;
  last_event_at: string | null;
  coverage_source: 'daily_rollup' | 'logical_outcomes+probe' | 'logical_outcomes' | 'probe' | 'none';
  window_minutes: number | null;
  probe_status: HarnessHealthDay['status'] | null;
}

export interface HarnessHealthResponse {
  private: true;
  window_days: number;
  generated_at: string;
  current_window_minutes?: number;
  current_period?: 'today_sao_paulo';
  current?: HarnessCurrentHealth[];
  subsystems: Partial<Record<HarnessSubsystem, HarnessHealthDay[]>>;
  llm_resilience: {
    logical_calls: number;
    successful_calls: number;
    fallback_calls: number;
    exhausted_calls: number;
    consecutive_calls_without_fallback: number;
    consecutive_first_attempt_successes: number;
    max_consecutive_first_attempt_successes: number;
    max_consecutive_first_attempt_successes_at: string | null;
    latest_logical_call_at: string | null;
    fallback_rate: number | null;
    exhaustion_rate: number | null;
  };
  health_events: Array<{
    id: string;
    subsystem: HarnessSubsystem;
    status: HarnessHealthDay['status'];
    signal_name: string;
    signal_value: number | null;
    occurred_at: string;
  }>;
  changes: Array<{
    id: string;
    event_type: string;
    subsystem: HarnessSubsystem;
    title: string;
    description: string | null;
    version_ref: string | null;
    experiment_key: string | null;
    occurred_at: string;
  }>;
}

export type HarnessErrorClassification =
  | 'policy_block'
  | 'idempotency'
  | 'billing'
  | 'accounts'
  | 'delivery'
  | 'content_limits'
  | 'workspace'
  | 'external_content'
  | 'external_resource'
  | 'persistence'
  | 'media_validation'
  | 'orchestration'
  | 'database'
  | 'runtime'
  | 'connectors'
  | 'scheduler'
  | 'auth'
  | 'stuck'
  | 'timeout'
  | 'rate_limit'
  | 'network'
  | 'storage'
  | 'contract'
  | 'configuration'
  | 'provider'
  | 'business_logic'
  | 'other';

export interface HarnessErrorCursor {
  before: string;
  before_source: string;
  before_id: string;
}

export interface HarnessErrorSummary {
  fingerprint: string;
  subsystem: HarnessSubsystem;
  component: string;
  classification: HarnessErrorClassification;
  severity: 'warning' | 'error' | 'critical';
  error_code: string | null;
  sample_message: string;
  occurrences: number | string;
  affected_tenants: number | string;
  first_occurred_at: string;
  last_occurred_at: string;
  source_tables: string[];
}

export interface HarnessErrorEvent {
  source_table: string;
  source_id: string;
  subsystem: HarnessSubsystem;
  component: string;
  classification: HarnessErrorClassification;
  severity: 'warning' | 'error' | 'critical';
  error_code: string | null;
  error_message: string;
  fingerprint: string;
  tenant_id: string | null;
  trace_id: string | null;
  task_id: string | null;
  demand_id: string | null;
  occurred_at: string;
}

export interface HarnessErrorsResponse {
  private: true;
  generated_at: string;
  window_hours: number;
  subsystem: HarnessSubsystem | null;
  complete_totals: true;
  total_occurrences: string;
  other_occurrences: string;
  summary_page: number;
  summary_page_size: 10 | 25 | 50 | 100;
  summary_total_groups: number;
  summary_total_pages: number;
  summary_has_previous: boolean;
  summary_has_next: boolean;
  summary: HarnessErrorSummary[];
  events_loaded: boolean;
  events_available: boolean | null;
  items: HarnessErrorEvent[];
  has_more: boolean;
  next_cursor: HarnessErrorCursor | null;
}

export interface HarnessAssetInventory {
  kind: 'agent' | 'skill' | 'loop';
  layer: 'all' | 'harness' | 'application';
  items: Array<Record<string, unknown> & { id: string; requires_admin: boolean }>;
}

/** DEM-2043 — preferências de Web Push: 3 controles independentes por usuário. */
export interface NotificationPreferences {
  demand_status_level: 'off' | 'terminal_only' | 'every_change';
  demand_pipeline_step_level: 'off' | 'every_change';
  skill_level: 'off' | 'terminal_only' | 'every_change';
}

/**
 * F-FINAL-66 (2026-05-26) — Resposta canônica de POST /v1/uploads.
 * Backend persiste em bucket `raios-tenant-assets/uploads/` e devolve signed URL
 * pra acesso temporário (TTL configurável, default 1h). `storage_path` é o ID
 * canônico — usar esse pra referenciar o arquivo em payloads downstream.
 */
/**
 * Reforma UX (DASH-07) — payload de reprovação com reenvio ao pipeline,
 * aceito como `feedback` no PATCH /v1/demands/:id/transition (backend
 * persiste em metadata.rejection_feedback).
 */
export interface DemandTransitionFeedback {
  /** Instruções do que precisa ser refeito/corrigido. */
  notes?: string;
  /** Step id do pipeline pra onde o trabalho volta. */
  return_step?: string;
  /** True quando o usuário marcou "Reenviar ao pipeline". */
  resubmit?: boolean;
}

export interface UploadFileResponse {
  upload_id: string;
  storage_path: string;
  signed_url: string;
  signed_url_ttl_seconds: number;
  mime_type: string;
  size_bytes: number;
  source: string;
  filename: string;
  uploaded_at: string;
}

/**
 * F-FINAL-69 (2026-05-26) — attachment shape mandado pro backend no wizardTurn.
 * Backend wizard-runner já aceita esse formato (raios PR #634+#635).
 */
export interface WizardAttachment {
  upload_id: string;
  storage_path: string;
  signed_url: string;
  mime_type: string;
  filename: string;
}

/**
 * Output canonico de skills (espelha contrato em raios/docs/skill-output-contract.md).
 * Campos conhecidos sao tipados; demais ficam livres via `[k: string]: unknown` pra
 * compat com outputs custom de skills antigas.
 */
// PR5 — Slash Engine types (admin GUI)
export type SlashHandlerType = 'internal_op' | 'skill' | 'agent' | 'script' | 'pipeline';
export type SlashSyncMode = 'sync' | 'async' | 'auto';
export type SlashStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface SlashCommandFull {
  id: string;
  tenant_id: string | null;
  slug: string;
  name: string;
  description: string | null;
  handler_type: SlashHandlerType;
  handler_ref: string;
  handler_config: Record<string, unknown>;
  args_schema: Record<string, unknown>;
  permissions: Record<string, unknown>;
  scopes: string[];
  sync_mode: SlashSyncMode;
  is_active: boolean;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export interface SlashCommandCreate {
  slug: string;
  name: string;
  description?: string;
  handler_type: SlashHandlerType;
  handler_ref: string;
  handler_config?: Record<string, unknown>;
  args_schema?: Record<string, unknown>;
  permissions?: Record<string, unknown>;
  scopes?: string[];
  sync_mode?: SlashSyncMode;
  is_active?: boolean;
  is_global?: boolean;
}

export interface SlashExecutionListItem {
  id: string;
  slash_command_id: string;
  channel: string;
  channel_user_id: string | null;
  status: SlashStatus;
  raw_input: string;
  handler_type: SlashHandlerType;
  handler_ref: string;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  trace_id: string;
}

export interface SlashExecutionFull extends SlashExecutionListItem {
  tenant_id: string;
  session_id: string | null;
  companion_id: string | null;
  parsed_args: Record<string, unknown>;
  child_task_id: string | null;
  child_execution_id: string | null;
  output: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  root_trace_id: string | null;
  parent_trace_id: string | null;
}

export interface SkillExecutionOutput {
  final_output?: string;
  result?: unknown;
  message?: string;
  summary?: string;
  steps?: unknown[];
  artifacts?: SkillArtifactOrGroup[];
  [k: string]: unknown;
}

// --- Shared types ---

/**
 * F8 (global-promotion) — resultado da tool `promote_asset` retornado pelas
 * rotas de promote (skill/agent/pipeline). A promoção pra global agora é
 * cascateada (skill → agentes dela; pipeline → skills + agentes) e a estrutura
 * (storage) é sincronizada. O backend dispara a tool e retorna o shape abaixo.
 *
 * Compat: as rotas antigas retornavam `{ status: 'pending' | 'applied' }`.
 * Mantemos `status` opcional pra não quebrar callers/asserts legados; o caminho
 * canonical agora é 201 + `promoted: true` (promoção direta concluída).
 */
export interface PromoteCascadeItem {
  kind: 'skill' | 'agent' | 'pipeline';
  slug: string;
  global_id?: string;
  reused?: boolean;
}

export interface PromoteMissingDep {
  kind: 'skill' | 'agent' | 'pipeline';
  slug: string;
}

export interface PromoteAssetResult {
  kind?: 'skill' | 'agent' | 'pipeline';
  source_id?: string;
  global_id?: string;
  slug?: string;
  /** Asset já era global e foi reaproveitado (idempotência). */
  reused?: boolean;
  /** Estrutura/storage sincronizada com sucesso. */
  synced?: boolean;
  verified?: string[];
  /** Dependências promovidas em cascata (skills + agentes referenciados). */
  cascade?: PromoteCascadeItem[];
  /** Dependências NÃO encontradas/promovidas — exige atenção do admin. */
  missing_deps?: PromoteMissingDep[];
  message?: string;
}

/**
 * Resposta unificada das rotas de promote. Inclui os campos legados
 * (`status`/`promotion_id`/`skill_id`/`agent`) e o resultado novo da tool.
 */
export interface PromoteResponse extends PromoteAssetResult {
  /** Legado: 'applied' (force) vs 'pending' (fila de review). Pode estar ausente. */
  status?: 'pending' | 'applied';
  promotion_id?: string;
  skill_id?: string;
  agent?: AgentDetail;
}

export interface TenantMe {
  id: string;
  name: string;
  slug: string;
  email: string;
  is_admin: boolean;
  onboarding_step: number;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface OnboardingStatus {
  current_step: number;
  completed_steps: number[];
  total_steps: number;
  onboarding_completed: boolean;
  next_action?: string;
  redirect?: string;
}

export interface DashboardOverview {
  tmi: {
    tmi: string;
    tmi_pillar: string;
    t2_items: string[];
    t3_items: string[];
    created_at: string;
  } | null;
  alerts: Array<Record<string, unknown>>;
  business_health: Array<{
    id: string;
    name: string;
    lifecycle_status: string;
    maturity_level: string;
    open_tasks: number;
    last_activity_at: string | null;
  }>;
  vision_scores: Array<{
    id: string;
    title: string;
    level: string;
    score: number | null;
    trend: string | null;
    last_scored_at: string | null;
  }>;
  last_activities: Array<{
    id: string;
    action_type: string;
    action_detail: string;
    source_module: string;
    created_at: string;
  }>;
  generated_at: string;
  // ─── Reforma UX 2026-06-10 (§7.2) — campos novos do overview. Opcionais
  // porque o backend antigo (pré-restart) não os envia; a UI degrada graceful.
  counts?: {
    businesses: { total: number; draft: number; active: number };
    agents: { total: number; tenant: number; global: number };
    skills: { total: number; tenant: number; global: number };
    pipelines: { total: number; tenant: number; global: number };
  };
  today_topics?: Array<{ topic: string; sessions_count: number; messages_count: number }>;
  demands_24h_by_status?: {
    ready: number;
    running: number;
    pending_approval: number;
    approved: number;
    done: number;
    rejected: number;
    blocked: number;
  };
}

export interface Vision {
  id: string;
  level: string;
  title: string;
  description?: string;
  target_score?: number;
  is_active: boolean;
  created_at: string;
}

export type BusinessType = 'product' | 'service' | 'content' | 'agency';
export type LifecycleStatus = 'draft' | 'setup' | 'active' | 'paused' | 'archived';
export type RevenueGoalType = 'autonomous' | 'assisted' | 'none';
export type MaturityLevel = 'standard' | 'independent' | 'tenant_independente';
export type CLevelId = 'cto' | 'cmo' | 'cfo' | 'coo' | 'cko' | 'cio';
export type CLevelStatus = 'active' | 'inactive' | 'shell';

export interface Business {
  id: string;
  tenant_id: string;
  name: string;
  type: BusinessType;
  subtype: string | null;
  lifecycle_status: LifecycleStatus;
  revenue_goal: RevenueGoalType;
  maturity_level: MaturityLevel;
  dna: Record<string, unknown>;
  /** Reforma UX §7.4 — logo do negócio (upload via /v1/uploads → PATCH). */
  logo_url?: string | null;
  /** Correção UX v2 W1.3 — contagens agregadas server-side. */
  demands_count?: number;
  repos_count?: number;
  github_repo_url?: string;
  github_owner?: string;
  github_repo?: string;
  default_branch?: string;
  pr_branch?: string;
  protected_branches?: string[];
  created_at: string;
  updated_at: string;
}

export interface BusinessDNA {
  id: string;
  tenant_id: string;
  business_id: string;
  market: string | null;
  audience: string | null;
  revenue_model: string | null;
  channels: string[];
  custom_metrics: string[];
  competitive_landscape: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CLevel {
  id: string;
  tenant_id: string;
  business_id: string;
  c_level_id: CLevelId;
  status: CLevelStatus;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface RevenueGoal {
  id: string;
  tenant_id: string;
  business_id: string;
  period: 'monthly' | 'quarterly' | 'yearly';
  period_start: string;
  target_amount: number;
  actual_amount: number;
  currency: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PersonalDNA {
  id: string;
  tenant_id: string;
  active_areas: string[];
  methodology_id: string;
  preferred_channels: string[];
  hats: string[];
  created_at: string;
  updated_at: string;
}

export interface LifeArea {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  methodology_id: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DailyPriority {
  id: string;
  tenant_id: string;
  date: string;
  tmi: string;
  tmi_pillar: string;
  t2_items: string[];
  t3_items: string[];
  completed: boolean;
  reflection: string | null;
  created_at: string;
  updated_at: string;
}

export interface TrackingEntry {
  id: string;
  type: string;
  value: unknown;
  notes?: string;
  created_at: string;
}

export interface Person {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  relationship?: string;
  tags?: string[];
  created_at: string;
}

export interface ContentItem {
  id: string;
  title?: string;
  text?: string;
  type?: string;
  status?: string;
  dikw_stage?: 'dado' | 'informacao' | 'conhecimento' | 'sabedoria';
  source?: string;
  source_module?: string;
  extracted?: Record<string, unknown>;
  created_at: string;
}

export type PRStatus = 'draft' | 'open' | 'review' | 'approved' | 'merged' | 'closed';

export interface PullRequest {
  id: string;
  tenant_id: string;
  repo: string;
  branch_source: string;
  branch_target: string;
  task_id: string | null;
  author_agent: string;
  status: PRStatus;
  reviewer_agent?: string;
  created_at: string;
  updated_at: string;
  demand_scope?: string | null;
  pipeline_id?: string | null;
  // DEM-2037 — colunas reais de pull_requests retornadas pela rota por demanda
  // (GET /v1/demands/:id/pull_requests). Opcionais pra não quebrar o shape
  // enriquecido de /pull-requests (lista do tenant).
  demand_id?: string | null;
  pr_number?: number | null;
  external_id?: string | null;
  trace_id?: string | null;
  root_trace_id?: string | null;
}

/**
 * DEM-2037 — artefato de entrega de uma demanda (GET /v1/demands/:id/artifacts).
 * Espelha a tabela `artifacts` (migrations 018 + DEM-2007 demand_id) + campos
 * DERIVADOS server-side pro viewer fullscreen multi-extensão: `url` (link
 * clicável/download), `extension` (extensão real pra escolher o renderer) e
 * `mime` quando disponível no metadata.
 */
export interface DemandArtifact {
  id: string;
  tenant_id: string;
  demand_id: string | null;
  type: string;
  subtype: string | null;
  code: string;
  title: string;
  description: string | null;
  dikw_stage: string | null;
  source_ref: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  /** Link resolvido (signed_url > url > pr_url > artifact_url > source_ref). */
  url: string | null;
  /** Extensão real derivada da URL/nome (md/html/pdf/png/...), ou null. */
  extension: string | null;
  /** MIME quando o metadata o trouxer, senão null. */
  mime: string | null;
}

export interface PersonRelationship {
  id: string;
  person_id: string;
  related_person_id: string;
  related_person_name?: string;
  relationship_type: string;
  context?: string;
  created_at: string;
}

export interface PersonEvidence {
  id: string;
  person_id: string;
  description: string;
  evidence_type: string;
  confidence: number;
  created_at: string;
}

export interface PersonInference {
  id: string;
  person_id: string;
  statement: string;
  confidence: number;
  basis?: string;
  created_at: string;
}

export interface PersonDetail extends Person {
  role?: string;
  relationships?: PersonRelationship[];
  evidences?: PersonEvidence[];
  inferences?: PersonInference[];
}

export interface Session {
  id: string;
  tenant_id: string;
  channel: 'cli' | 'telegram' | 'voice' | 'dashboard';
  status: 'active' | 'ended';
  started_at: string;
  ended_at: string | null;
  messages: SessionMessage[];
  message_count?: number;
  /** Reforma UX §7.1 — título editável (PATCH /v1/sessions/:id). */
  title?: string | null;
  /** Reforma UX §7.1 — resumo derivado de metadata.summary. */
  summary?: string | null;
  /** Correção UX v2 §5.2.6 — runtime persistido na criação ('raios' | 'openclaw' | ...); null em sessões antigas (frontend usa heurística como fallback). */
  runtime?: string | null;
  companion_id?: string | null;
  created_at?: string;
  /** Última atividade — usada pra bolinha amarela (aberta porém ociosa). */
  updated_at?: string | null;
  metadata?: Record<string, unknown> | null;
  context?: Record<string, unknown> | null;
}

export interface SessionMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface DailyPriorityFast {
  tmi: string;
  tmi_pillar: string;
  t2_items: string[];
  t3_items: string[];
  date: string;
}

export interface LlmStatusResponse {
  connected: boolean;
  provider: string | null;
  model: string | null;
}

export interface LlmAccount {
  id: string;
  provider: string;
  display_name: string;
  model_id: string;
  /** Modelo secundário desta conta; null remove o fallback account-scoped. */
  fallback_model_id?: string | null;
  is_active: boolean;
  is_default: boolean;
  label: string;
  last_used_at: string | null;
  created_at: string;
}

export type ConnectionStatus = 'connected' | 'rate_limited_session' | 'rate_limited_weekly' | 'expiring' | 'expired' | 'unknown';

/**
 * Janela de quota da conta (sessão/semana), normalizada e agnóstica de provider.
 * Vem de llm_accounts.quota_snapshot (capturada no uso real, no 429 e no probe
 * periódico). A UI renderiza uma barra por window.
 */
export interface QuotaWindow {
  /**
   * session/weekly = Anthropic/OpenAI (5h/7d).
   * rpm/tpm = Grok/xAI rate-limit rolling 1m (DEM-2580; sem 5h/week fabricado).
   */
  key: 'session' | 'weekly' | 'rpm' | 'tpm';
  label: string;
  /** Fração usada 0..1 (ex.: 0.08 = 8%). null se o provider não informou. */
  utilization: number | null;
  status: string | null;
  /** Quando a janela reseta (ISO). null se ausente. */
  resets_at: string | null;
}

export interface LlmAccountWithStatus {
  id: string;
  provider: string;
  model_id: string;
  /** Fallback exclusivo desta conta, sempre do mesmo provider. */
  fallback_model_id: string | null;
  /**
   * Identidade legível devolvida pela rota autenticada do próprio tenant
   * (por exemplo, email no OAuth Anthropic ou nome da conexão no provider).
   */
  display_name: string;
  label: string;
  connection_status: ConnectionStatus;
  expires_at: string | null;
  is_active: boolean;
  cooldown_until: string | null;
  current_sessions: number;
  /** v5: 'oauth' | 'api_key' | 'adc'. null em rows legadas. */
  auth_method: AuthMethod | null;
  /** Quota de sessão (5h) e semanal (7d). null/ausente até a 1ª captura. */
  quota_windows: QuotaWindow[] | null;
  /** Quando o snapshot de quota foi capturado (ISO). */
  quota_captured_at: string | null;
  /**
   * DEM-2631: probe rodou sem % real. Card mostra mensagem explícita
   * em vez de sumir a barra (silêncio ambíguo). null quando ok/sem probe.
   */
  quota_probe_degraded?: {
    kind: string;
    message: string;
    http_status?: number | null;
    top_level_keys?: string[];
  } | null;
}

export interface LlmResilienceAttempt {
  id: string;
  tenant_id: string;
  logical_call_id: string;
  agent_execution_id: string | null;
  pipeline_run_id: string | null;
  task_id: string | null;
  agent_id: string | null;
  agent_version: string | null;
  route_index: number;
  attempt_index: number;
  provider: string;
  model_key: string;
  account_id: string;
  classification: string;
  outcome: 'started' | 'succeeded' | 'failed' | 'advanced';
  error_code: string | null;
  http_status: number | null;
  backoff_ms: number;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  tool_calls_before: number;
  tool_calls_after: number;
  delivery_state: string | null;
  created_at: string;
}

export interface LlmResilienceLogicalCall {
  logical_call_id: string;
  agent_execution_id: string | null;
  attempt_count: number;
  fallback_depth: number;
  winning_route: { provider: string; model_key: string; account_id: string } | null;
  delivery_state: string | null;
  observability_degraded: boolean | null;
  metadata_consistent: boolean | null;
}

export interface LlmResilienceAttemptsResponse {
  items: LlmResilienceAttempt[];
  logical_calls: LlmResilienceLogicalCall[];
  pagination: { page: number; per_page: number; total: number; total_pages: number; has_next: boolean };
}

export interface LlmResilienceSummary {
  window: { since: string; until: string };
  partial: boolean;
  limits: { max_attempts: number; returned_attempts: number; total_attempts: number };
  totals: {
    attempts: number;
    logical_calls: number;
    fallback_calls: number;
    exhausted_calls: number;
    successful_calls: number;
    observability_degraded_executions: number;
    metadata_mismatches: number;
  };
  rates: { fallback: number; exhaustion: number };
  latency_ms: { average: number | null; p95: number | null };
  by_route: Array<{
    provider: string;
    model_key: string;
    account_id: string;
    attempts: number;
    successes: number;
    failures: number;
    advanced: number;
    average_duration_ms: number | null;
  }>;
}

export interface LlmResilienceFilters {
  logical_call_id?: string;
  agent_execution_id?: string;
  status?: LlmResilienceAttempt['outcome'];
  provider?: string;
  model_key?: string;
  account_id?: string;
  page?: number;
  per_page?: number;
}

export interface AdminLlmResilienceFilters extends LlmResilienceFilters {
  tenant_id: string;
}

// ─── Multi-provider v5 (DEM-01C/02/03/06/07) ────────────────────────────────

export type AuthMethod = 'oauth' | 'api_key' | 'adc';
export type CanonicalEffort = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type Modality = 'text' | 'image' | 'tts' | 'stt' | 'embedding' | 'video' | 'realtime';

export interface LlmProvider {
  id: string;
  key: string; // 'anthropic' | 'openai' | ...
  display_name: string;
  site_url: string | null;
  supported_auth_methods: AuthMethod[];
  canonical_top_model_key: string | null;
  config: { reasoning_map?: Record<string, unknown> };
  status: 'active' | 'preview' | 'deprecated';
}

export interface LlmModel {
  id: string;
  provider_id: string;
  model_key: string;
  display_name: string;
  modality: Modality;
  tier: string;
  supported_effort_levels: CanonicalEffort[];
  default_effort_level: CanonicalEffort | null;
  input_cost_per_m: number | null;
  output_cost_per_m: number | null;
  alt_unit_cost: number | null;
  alt_unit_label: string | null;
  status: 'active' | 'available_via_oauth' | 'preview' | 'deprecated';
  released_at: string | null;
  deprecated_at: string | null;
  notes: string | null;
  max_output_tokens?: number | null;
  /** Reforma UX §7.3 — flags de disponibilidade por método de auth. */
  available_via_oauth?: boolean;
  available_via_api_key?: boolean;
}

export interface AgentInstance {
  id: string;
  agent_id: string;
  display_name: string | null;
  config_overrides: {
    provider?: string;
    model_key?: string;
    reasoning_effort?: CanonicalEffort;
    llm_account_id?: string;
    fallback_chain?: Array<{ provider: string; model_key: string; reasoning_effort?: CanonicalEffort }>;
  };
  state: 'active' | 'paused' | 'deprecated';
  ema_score: number | null;
  ema_sessions: number | null;
  promotion_status: string;
  created_at: string;
  updated_at: string;
  agents?: { slug: string; name: string; archetype: string; scope: string };
}

export interface EphemeralExecution {
  id: string;
  runner_type: string;
  trace_id: string;
  provider: string | null;
  model_key: string | null;
  modality: Modality | null;
  reasoning_effort: CanonicalEffort | null;
  status: 'running' | 'ok' | 'failed';
  tokens_input: number;
  tokens_output: number;
  cost_usd: number;
  started_at: string;
  ended_at: string | null;
  output: { content?: string; error?: string } | null;
}

export interface WizardResolveResponse {
  source: string;
  user_md: string;
  msg_count?: number;
  message?: string;
}

export interface TelegramConnectResponse {
  deep_link: string;
  connect_token: string;
  expires_at: string;
}

export interface TelegramStatusResponse {
  connected: boolean;
  telegram_username: string | null;
  connected_at: string | null;
}

export interface OrchestrateResult {
  parent_task_id: string;
  status: 'queued';
  sizing: 'CURTA' | 'MEDIA' | 'LONGA';
}

export interface BusinessRepo {
  id: string;
  tenant_id: string;
  business_id: string;
  label: string;
  github_repo_url: string | null;
  github_owner: string | null;
  github_repo: string | null;
  github_token_encrypted: string | null; // '[SET]' when present
  default_branch: string;
  pr_branch: string;
  protected_branches: string[];
  created_at: string;
  updated_at: string;
}

// DEM-2121 F5 — domínios verificáveis por business via DNS TXT.
export type DomainVerificationStatus = 'pending' | 'verified' | 'failed' | 'expired';

export interface BusinessDomain {
  id: string;
  tenant_id: string;
  business_id: string;
  domain: string;
  is_primary: boolean;
  verification_token: string;
  verification_status: DomainVerificationStatus;
  verified_at: string | null;
  last_checked_at: string | null;
  token_expires_at: string;
  created_at: string;
  updated_at: string;
  // Campos derivados (backend): host do TXT e o valor esperado.
  verification_host: string;
  txt_record: string;
}

export interface DomainVerifyResult {
  verified: boolean;
  status: DomainVerificationStatus;
  message: string;
  host: string;
  expected: string;
  found: string[];
  domain: BusinessDomain;
}

export interface AddBusinessRepoParams {
  label: string;
  repo_url: string;
  token: string;
  default_branch?: string;
  pr_branch?: string;
}

// DEM-2123 — Marketing/Branding assets (path canônico no Storage).
export type BusinessAssetKind = 'branding' | 'guideline' | 'asset';

export interface BusinessAsset {
  id: string;
  tenant_id: string;
  business_id: string;
  kind: BusinessAssetKind;
  filename: string;
  storage_path: string;
  content_type: string;
  size_bytes: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

// --- Tasks ---

/**
 * Status canônico alinhado com tasks_status_check (migration 20260430120700):
 * 'pending','running','retry','done','failed','cancelled','paused'.
 * Backend TS interno usa CAPS (TaskStatus em src/shared/types/tasks.ts), API
 * retorna lowercase pra frontend.
 */
export type BackendTaskStatus =
  | 'pending'
  | 'running'
  | 'retry'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'paused';

export interface BackendTask {
  id: string;
  tenant_id: string;
  event_type: string;
  status: BackendTaskStatus;
  handler: string;
  /** Só presente em GET /tasks/:id (detail). Lista paginada faz select enxuto e omite. */
  input?: Record<string, unknown>;
  /** Só presente em GET /tasks/:id (detail). */
  output?: Record<string, unknown>;
  /** Só presente em GET /tasks/:id (detail). */
  error?: Record<string, unknown>;
  parent_task_id?: string | null;
  idempotency_key?: string;
  retry_count: number;
  max_retries: number;
  sizing: string;
  created_at: string;
  updated_at: string;
  // Fase 1.B+1.C — adicionados PR1+PR3
  demand_id?: string | null;
  pipeline_run_id?: string | null;
  target_slug?: string | null;
  // Kanban perf 2026-05-06 — campos do card rico
  urgency_level?: string | null;
  title?: string | null;
  description?: string | null;
  objective?: string | null;
  session_id?: string | null;
  /**
   * NOTA: tabela `tasks` NÃO tem coluna `companion_id` (só
   * companion_messages/companion_sessions/agent_executions têm). Sempre será
   * null em tasks. Mantido aqui só pra back-compat de código que assume o
   * campo opcional — UI cai em fallback.
   */
  companion_id?: string | null;
  agent_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  estimated_duration_seconds?: number | null;
  /**
   * G4 F1 (D-G4-F): renamed from duration_seconds. Backend G3 PR-P1 dropou
   * coluna tasks.duration_seconds → tasks.duration_ms BIGINT é canonical.
   */
  duration_ms?: number | null;
  delta_estimated_actual_seconds?: number | null;
  // Modal storytelling 2026-05-07 — campos semânticos da row tasks.
  // Vêm via GET /tasks/:id (select('*')); não vêm na lista paginada.
  acceptance_criteria?: string[] | string | null;
  acceptance_criteria_lineage?: Record<string, unknown> | null;
  definition_of_done?: string[] | string | null;
  anti_definition_of_done?: string[] | string | null;
  expected_artifacts?: string[] | string | null;
  expected_input?: string | null;
  expected_output?: string | null;
  preconditions?: string[] | string | null;
  postconditions?: string[] | string | null;
  decision_rationale?: string | null;
  rationale?: string | null;
  real_problem?: string | null;
  risk_if_skipped?: string | null;
  historical_context?: string | null;
  gotchas?: string[] | string | null;
  evidence_refs?: string[] | null;
  execution_notes?: string | null;
  validation_commands?: string[] | string | null;
  tools_allowed?: string[] | null;
  affected_parties?: string[] | null;
  outcome?: string | null;
  domain?: string | null;
  supervision_mode?: string | null;
  autonomy_level_expected?: string | null;
  estimated_size?: string | null;
  source_ref?: string | null;
  trace_id?: string | null;
  root_trace_id?: string | null;
  parent_trace_id?: string | null;
  created_by?: string | null;
  closed_by?: string | null;
  pipeline_step_id?: string | null;
  parent_handoff_id?: string | null;
  preflight_result_id?: string | null;
  final_handoff?: Record<string, unknown> | null;
  original_literal_context?: string | null;
  dependencies_external?: string[] | null;
  batch_index?: number | null;
  confidence?: number | null;
}

/** Resposta paginada de /v1/tasks (Kanban perf 2026-05-06). */
export interface TasksListResponse {
  items: BackendTask[];
  total: number;
  limit: number;
  offset: number;
}

/** Resposta de /v1/tasks/stats (Kanban perf 2026-05-06). */
export interface TasksStatsResponse {
  counts: Record<string, number>;
  avg_duration_seconds_by_handler: Record<string, number>;
}

// PR1: handlers, internal_ops, wizards, routes
export interface Handler {
  id: string;
  name: string;
  kind: 'task-bound' | 'demand-bound' | 'project-bound' | 'vision-bound';
  handler_type: 'internal_op' | 'skill' | 'agent' | 'pipeline' | 'script' | 'wizard';
  handler_module: string | null;
  description: string | null;
  config: Record<string, unknown>;
  permissions: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HandlerExecution {
  id: string;
  handler_id: string;
  target_slug: string | null;
  kind: string;
  trace_id: string;
  origin: string;
  origin_entity_type: string | null;
  origin_entity_id: string | null;
  tenant_id: string;
  task_id: string | null;
  demand_id: string | null;
  pipeline_run_id: string | null;
  wizard_execution_id: string | null;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
}

export interface WizardExecution {
  id: string;
  tenant_id: string;
  wizard_id: string | null;
  demand_id: string | null;
  artifact_id: string | null;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'abandoned' | 'failed';
  channel: string;
  channel_user_id: string;
  state: Record<string, unknown>;
  step: string | null;
  expires_at: string;
  last_activity_at: string;
  paused_at: string | null;
  paused_reason: string | null;
  resumed_count: number;
  session_token: string | null;
  child_handler_execution_id: string | null;
  started_at: string;
  completed_at: string | null;
}

// D4 (2026-05-04) — tools como entidade canônica
export interface AdminTool {
  slug: string;
  name: string;
  description: string;
  category: 'dispatch' | 'query' | 'write' | 'external' | 'storage' | 'mcp';
  is_mutating: boolean;
  requires_admin: boolean;
  /** Core: auto-attached nos agentes. Com is_companion_only, só nos companions. */
  is_core?: boolean;
  /** Específica de companions (com is_core: core só pros companions). */
  is_companion_only?: boolean;
  /** Exposta ao MCP (runtime externo OpenClaw). */
  mcp_exposed?: boolean;
  runner_module: string;
  scope: 'global' | 'tenant';
  tenant_id: string | null;
  is_active: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  /** Apenas no detail endpoint */
  agents_count?: number;
  /** Apenas no detail endpoint */
  schema_json?: Record<string, unknown>;
}

export interface AdminAgentTool {
  agent_id: string;
  tool_slug: string;
  enabled: boolean;
  config_override: Record<string, unknown>;
  created_at: string;
  /** Resolvidos pelo backend — UI mostra legível, não o UUID. */
  agent_slug?: string | null;
  agent_name?: string | null;
  agent_archetype?: string | null;
}

export interface AdminToolExecution {
  id: string;
  tool_slug: string;
  tenant_id: string;
  agent_id: string | null;
  agent_execution_id: string | null;
  parent_handler_execution_id: string | null;
  trace_id: string;
  status: 'running' | 'completed' | 'failed';
  error: { message?: string } | null;
  is_mutating: boolean;
  started_at: string;
  completed_at: string | null;
  duration_ms: number | null;
}

// F6.1 (2026-05-22) — Wizard schema dinâmico (GET /v1/wizards/:slug/schema).
// Reflete output do backend `deriveFieldsFromContract` (src/wizards/field-derivation.ts).
export type WizardFieldType =
  | 'string'
  | 'text'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'array'
  | 'object';

export interface WizardField {
  name: string;
  type: WizardFieldType;
  required: boolean;
  enum?: string[];
  description?: string;
  /**
   * F-FINAL-38 (2026-05-25): endpoint que retorna lista de itens selecionáveis
   * pra fields tipo `array<string>` com lookup dinâmico (allowed_tools,
   * allowed_skills). Frontend usa pra renderizar multi-select com checkboxes
   * ao invés de textarea-JSON.
   */
  lookup_endpoint?: string;
}

// F-FINAL-38: shapes dos endpoints /v1/tools/available e /v1/skills/available.
export interface ToolAvailableItem {
  slug: string;
  name: string;
  description?: string;
  category?: string;
}
export interface ToolsAvailableResponse {
  core: ToolAvailableItem[];
  options: ToolAvailableItem[];
}
export interface SkillAvailableItem {
  slug: string;
  name: string;
  scope: 'global' | 'tenant';
  description?: string;
}
export interface SkillsAvailableResponse {
  options: SkillAvailableItem[];
}

/**
 * F-FINAL-41 (2026-05-25): wave UX-curada do wizard (mirror de
 * `wizards.steps_definition` no DB). `fields` aqui sao as keys que o admin
 * preenche em conversacao (collected payload), NAO necessariamente colunas DB.
 *
 * Source-of-truth pra barra de progresso visual: frontend conta segmentos por
 * `length` e mapeia fill por presenca dos fields em `collected`.
 */
export interface WizardStep {
  wave: number;
  name: string;
  fields: string[];
}

export interface WizardSchemaResponse {
  wizard_slug: string;
  artifact_type: string;
  entity_type: string;
  fields: WizardField[];
  contract_versions: { create?: string; update?: string };
  /**
   * F-FINAL-41 (2026-05-25): waves UX-curadas (wizards.steps_definition).
   * Opcional pra retro-compat com backend pre-F-FINAL-41 — quando ausente,
   * frontend cai pra heuristica local (ver AgentWizardChat WAVE_SEGMENTS).
   */
  steps?: WizardStep[];
  /** F-FINAL-41 (2026-05-25): conveniencia (= steps.length). Pode estar ausente. */
  total_waves?: number;
}

// Resposta canonica do submit. Backend retorna ao menos artifact_id+slug;
// shape flexível pra suportar 3 artifact types (skill/agent/pipeline).
export interface WizardSubmitResponse {
  artifact_id: string;
  slug: string;
  redirect_url?: string;
  [k: string]: unknown;
}

// D8 (2026-05-04) — wizard seeded row pra AdminWizardsPage
export interface AdminWizard {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  artifact_type: string;
  conductor_agent_slug: string;
  pause_after_minutes: number;
  ttl_minutes: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Admin endpoint retorna shape mais reduzido que WizardExecution full
export type AdminWizardExecution = Pick<
  WizardExecution,
  | 'id'
  | 'wizard_id'
  | 'tenant_id'
  | 'channel'
  | 'channel_user_id'
  | 'state'
  | 'step'
  | 'status'
  | 'demand_id'
  | 'artifact_id'
  | 'started_at'
  | 'completed_at'
  | 'last_activity_at'
  | 'paused_at'
  | 'paused_reason'
  | 'resumed_count'
  | 'expires_at'
>;

export interface RouteRow {
  id: string;
  event_type: string;
  handler_name: string;
  target_slug: string | null;
  sizing: 'MICRO' | 'CURTA' | 'MEDIA' | 'LONGA';
  mode: 'single' | 'chain' | 'dag';
  chain_config: Array<Record<string, unknown>>;
  steps_config: Array<Record<string, unknown>>;
  runner: string | null;
  is_active: boolean;
}

export interface DemandProgress {
  demand_id: string;
  code: string;
  tenant_id: string;
  title: string;
  demand_status: string;
  tasks_planned: number;
  tasks_created: number;
  tasks_done: number;
  tasks_failed: number;
  tasks_cancelled: number;
  tasks_active: number;
  scope_growth_rate: number | null;
  success_rate: number | null;
  failure_rate: number | null;
  last_task_completed_at: string | null;
  created_at: string;
  completed_at: string | null;
}

// --- Demands ---

// 13 status canonical do backend (src/demands/types.ts DemandStatus enum).
// REMOVIDOS (legacy, não existem mais no enum DB): 'executing' (→ 'running'),
// 'ready_for_review' (→ 'review'), 'adjustment'.
// ADICIONADOS: 'planned' (post-draft pre-ready), 'running' (substitui
// 'executing'), 'review' (substitui 'ready_for_review'), 'finishing'
// (intermediate split sync/async), 'failed' (terminal via rollup).
export type DemandStatus =
  | 'draft'
  | 'planned'
  | 'ready'
  | 'running'
  | 'pending_approval'
  | 'review'
  | 'approved'
  | 'rejected'
  | 'finishing'
  | 'done'
  | 'failed'
  | 'cancelled'
  | 'blocked';

export type DemandPriority = 'critical' | 'high' | 'medium' | 'low';
export type RuntimeTarget = 'raios' | 'arqueops' | 'openclaw' | 'manual';
export type SupervisionMode = 'supervised' | 'unsupervised' | 'auto';

export interface DemandExecutionExecutor {
  type: 'pipeline' | 'squad';
  slug: string;
  name: string;
  version: string;
}

export interface Demand {
  id: string;
  // Código humano-legível da demand (ex: DEM-1830). Coluna `code` na tabela
  // demands; a API retorna mas o type não declarava. Opcional pra cobrir rows
  // antigas sem code.
  code?: string | null;
  tenant_id: string;
  title: string;
  description: string | null;
  status: DemandStatus;
  priority: DemandPriority;
  project_id: string | null;
  project_name: string | null;
  runtime_target: RuntimeTarget;
  supervision_mode: SupervisionMode;
  acceptance_criteria: string[] | null;
  executor: string | null;
  trace_id: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  // F4 2026-07-19 — Size da demanda (Micro/Simples/Média/Grande/Épico; a API
  // retorna a coluna, o type não declarava — quebrou o tsc -b da main no #282).
  sizing?: string | null;
  // PR1+PR4 — métricas + spawn
  tasks_planned?: number;
  tasks_created?: number;
  tasks_done?: number;
  tasks_failed?: number;
  tasks_cancelled?: number;
  scope_growth_rate?: number | null;
  success_rate?: number | null;
  failure_rate?: number | null;
  last_task_completed_at?: string | null;
  spawn_handler_slug?: string | null;
  // Card rico 2026-05-06 — campos opcionais que vêm da row do DB
  objective?: string | null;
  outcome_expected?: string | null;
  scope_in?: string | null;
  scope_out?: string | null;
  // DEM-2001 — campos semânticos completos da demanda (preenchidos pelo
  // demand-refinement no pipeline canonical v5.1). O backend retorna a row
  // inteira (select '*'), mas o type não os declarava — o frontend deixava de
  // exibir `context` e `delivery_artifact_expected`. Agora declarados pra que a
  // tela de detalhe os mostre de forma fiel ao estado do backend.
  context?: string | null;
  /** Artefato de entrega esperado — o que o usuário avalia como "pronto". */
  delivery_artifact_expected?: string | null;
  /** Negócio dono da demanda (migration 20240101000073). */
  business_id?: string | null;
  /**
   * DEM-2162 — tenant DONO da demanda (join com tenants). Populado pela API de
   * listagem quando há colaboração de business. Alimenta o seletor dinâmico de
   * dono e o badge por card (genérico pra N colaboradores, nunca hardcoded).
   */
  owner_tenant?: { id: string; name: string | null } | null;
  due_at?: string | null;
  ready_at?: string | null;
  started_at?: string | null;
  estimated_duration_hours?: number | null;
  reviewer_id?: string | null;
  reviewer_type?: string | null;
  approver_id?: string | null;
  review_artifact_required?: string | null;
  review_package_type?: string | null;
  recurrence_expected?: string | null;
  intervention_points?: string[] | null;
  origin_type?: string | null;
  origin_memory_id?: string | null;
  pipeline_slug?: string | null;
  execution_mode?: 'pipeline' | 'squad' | null;
  execution_target_id?: string | null;
  execution_target_version_id?: string | null;
  execution_executor?: DemandExecutionExecutor | null;
  metadata?: Record<string, unknown> | null;
  // P1.12 Frente Q — pipeline canonical v3.0.0
  // - parent_pipeline_run_id: demands `pending_approval` criadas via débito
  //   max_backward_depth referenciam o run pai aqui.
  // - final_handoff: discriminated union {to_type, to_ref, delivery_format,
  //   ...}. Presente em demands criadas pelo @pipeline-runner.
  // - estimated_duration_seconds: ETA canônico sistema-wide (R12). Coexiste
  //   com `estimated_duration_hours` legacy via dual-read em UIs.
  parent_pipeline_run_id?: string | null;
  final_handoff?: Record<string, unknown> | null;
  estimated_duration_seconds?: number | null;
}

export interface DemandFilters {
  status?: string;
  priority?: string;
  project_id?: string;
  runtime_target?: string;
  /** Correção UX v2 W1.3 — filtro server-side por negócio. */
  business_id?: string;
  /**
   * DEM-2162 — filtro por DONO da demanda: 'mine' | '<tenant_id>' | 'all'
   * (default 'all' no backend). Genérico pra N colaboradores.
   */
  owner?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

/**
 * DEM-2079 (#3) — motivo REAL do bloqueio de uma demand, interpretado
 * server-side a partir de pipeline_runs.config.execution_state.blocked_reason.
 * Substitui o texto HARDCODED que a UI mostrava ("falhou no decomposer").
 * `raw` é o objeto blocked_reason inteiro — alimenta o "Detalhes técnicos"
 * colapsável (Expandir). Campos interpretados podem ser null (best-effort).
 */
export interface DemandBlockedInfo {
  /** slug do step onde bloqueou (ex.: 'peer-review'). */
  step_slug: string | null;
  /** número 1-based do step (step_index + 1) — NUNCA o 0-based cru. */
  step_number: number | null;
  /** agente que atuava no step (ex.: 'jeremias'). */
  agent: string | null;
  /** código-máquina do motivo (ex.: 'agent_output_status_not_ok'). */
  reason_code: string | null;
  /** mensagem técnica do agente/erro (ex.: 'agent_output_status_not_ok:bezalel:blocked'). */
  message: string | null;
  /** decisão do pipeline (ex.: 'agent_output_blocked_no_retry'). */
  decision_reason: string | null;
  /** timestamp ISO do bloqueio, quando disponível. */
  blocked_at: string | null;
  /** objeto blocked_reason completo (detalhe técnico colapsável). */
  raw: Record<string, unknown>;
}

/**
 * Resposta de GET /v1/demands.
 *
 * Backend (2026-05-06) retorna `{ items, total, page, per_page }`. Mantemos
 * `demands` como alias opcional pra back-compat com clients antigos que
 * liam `response.demands`. Frontend novo deve preferir `items`.
 */
export interface DemandListResponse {
  items?: Demand[];
  /** @deprecated use `items` */
  demands?: Demand[];
  total: number;
  page: number;
  per_page: number;
}

export interface ProjectSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
}

export interface TraceEvent {
  id: string;
  trace_id: string;
  event_type: string;
  actor: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface TraceChain {
  trace_id: string;
  demand_id: string;
  events: TraceEvent[];
  tasks: BackendTask[];
  approvals: TraceEvent[];
  feedbacks: TraceEvent[];
}

// --- C-Level labels ---
export const C_LEVEL_LABELS: Record<CLevelId, { name: string; description: string }> = {
  cto: { name: 'CTO', description: 'Chief Technology Officer — Tecnologia e sistemas' },
  cmo: { name: 'CMO', description: 'Chief Marketing Officer — Marketing e vendas' },
  cfo: { name: 'CFO', description: 'Chief Financial Officer — Financas e contabilidade' },
  coo: { name: 'COO', description: 'Chief Operating Officer — Operacoes e processos' },
  cko: { name: 'CKO', description: 'Chief Knowledge Officer — Conhecimento e dados' },
  cio: { name: 'CIO', description: 'Chief Information Officer — Informacao e inteligencia' },
};

/**
 * F-FINAL-81 (2026-05-27): erro tipado da camada de API que carrega o HTTP
 * status. Permite que callers decidam fallback (ex: polling em 502/504 do
 * proxy Vercel quando o turn é síncrono mas o backend demora >60s).
 *
 * Antes lançávamos `Error` cru e o caller precisava parsear a mensagem; agora
 * `instanceof ApiError` + `err.status` é o caminho canonical.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;

  constructor(opts: { message: string; status: number; code?: string; requestId?: string }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.requestId = opts.requestId;
  }
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('raios_token', token);
    } else {
      localStorage.removeItem('raios_token');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('raios_token');
    }
    return this.token;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...((options.headers as Record<string, string>) ?? {}),
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    // Lê como text primeiro pra poder reportar conteúdo bruto se JSON for
    // inválido. Antes, .json() direto explodia com "Unexpected non-whitespace
    // character" sem revelar QUAL body chegou — impossibilitava debug.
    const rawText = await res.text();
    let json: ApiResponse<T>;
    try {
      json = rawText ? JSON.parse(rawText) : ({} as ApiResponse<T>);
    } catch {
      const bodyPreview = rawText.slice(0, 300).replace(/\s+/g, ' ');
      // F-FINAL-81: 502/504 do proxy Vercel costuma vir como HTML em vez de
      // JSON. Preservar status pra caller detectar o caso de proxy timeout.
      throw new ApiError({
        message: `Resposta inválida do servidor (HTTP ${res.status}, ${path}): ${bodyPreview}`,
        status: res.status,
      });
    }

    if (!res.ok || !json.success) {
      if (res.status === 401) {
        // 401 do PROPRIO login = credencial invalida, NAO sessao expirada.
        // Antes, QUALQUER 401 (inclusive senha errada/typo no e-mail) disparava
        // logout + redirect + "Sessao expirada" — na tela de login isso vira um
        // reload enganoso e o erro real nunca aparece (incidente 07/07/2026:
        // e-mail digitado .con, backend respondeu Invalid login credentials e o
        // user viu "Sessao expirada. Redirecionando...").
        if (path === '/v1/auth/login') {
          throw new ApiError({
            message: 'E-mail ou senha inválidos.',
            status: 401,
            code: json.error?.code,
            requestId: json.error?.request_id,
          });
        }
        this.logout();
        window.location.href = '/login';
        throw new ApiError({
          message: 'Sessao expirada. Redirecionando para login...',
          status: 401,
        });
      }
      throw new ApiError({
        message: json.error?.message ?? `Erro ${res.status}`,
        status: res.status,
        code: json.error?.code,
        requestId: json.error?.request_id,
      });
    }

    return json.data;
  }

  // Auth
  async login(email: string, password: string) {
    const data = await this.request<{
      access_token: string;
      refresh_token: string;
      expires_at: number;
      user: { id: string; email: string };
    }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token);
    localStorage.setItem('raios_refresh', data.refresh_token);
    return data;
  }

  async refreshToken() {
    const refresh = localStorage.getItem('raios_refresh');
    if (!refresh) throw new Error('No refresh token');
    const data = await this.request<{
      access_token: string;
      refresh_token: string;
    }>('/v1/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refresh }),
    });
    this.setToken(data.access_token);
    localStorage.setItem('raios_refresh', data.refresh_token);
    return data;
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('raios_refresh');
  }

  // Health
  async healthLive() {
    return this.request<{ status: string }>('/health/live');
  }

  async healthReady() {
    return this.request<{
      status: string;
      latency_ms: number;
      checks: { database: string; pgvector: string };
    }>('/health/ready');
  }

  async getHarnessHealth(days = 90, fresh = false) {
    // fresh=1 (2026-08-04): o botão Atualizar fura o cache de 30s do backend
    // (guarda anti-rajada de 5s no servidor); o auto-refresh usa o cache normal.
    const freshParam = fresh ? '&fresh=1' : '';
    return this.request<HarnessHealthResponse>(
      `/v1/admin/harness/health?days=${Math.min(Math.max(days, 1), 90)}${freshParam}`,
    );
  }

  async getHarnessErrors(input: {
    hours?: number;
    limit?: number;
    summaryPage?: number;
    summaryLimit?: 10 | 25 | 50 | 100;
    includeEvents?: boolean;
    subsystem?: HarnessSubsystem;
    cursor?: HarnessErrorCursor | null;
  } = {}) {
    const params = new URLSearchParams({
      hours: String(Math.min(Math.max(input.hours ?? 48, 1), 168)),
      limit: String(Math.min(Math.max(input.limit ?? 50, 1), 200)),
      summary_page: String(Math.max(1, Math.floor(input.summaryPage ?? 1))),
      summary_limit: String(input.summaryLimit ?? 25),
    });
    if (input.subsystem) params.set('subsystem', input.subsystem);
    if (input.includeEvents) params.set('include_events', '1');
    if (input.cursor) {
      params.set('before', input.cursor.before);
      params.set('before_source', input.cursor.before_source);
      params.set('before_id', input.cursor.before_id);
    }
    return this.request<HarnessErrorsResponse>(
      `/v1/admin/harness/errors?${params.toString()}`,
    );
  }

  async listHarnessAssets(
    kind: HarnessAssetInventory['kind'],
    layer: HarnessAssetInventory['layer'] = 'all',
  ) {
    return this.request<HarnessAssetInventory>(
      `/v1/admin/harness/assets?kind=${kind}&layer=${layer}&limit=500`,
    );
  }

  // ─── Web Push (DEM-2043) ───────────────────────────────────────────────
  async getVapidPublicKey() {
    return this.request<{ public_key: string }>('/v1/push/vapid-public-key');
  }

  async savePushSubscription(body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    device?: string;
  }) {
    return this.request<{ subscription: { id: string; endpoint: string; device: string | null; enabled: boolean } }>(
      '/v1/push/subscriptions',
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  async deletePushSubscription(endpoint: string) {
    return this.request<{ removed: boolean }>('/v1/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
  }

  async getNotificationPreferences() {
    return this.request<{ preferences: NotificationPreferences }>('/v1/push/preferences');
  }

  async updateNotificationPreferences(prefs: NotificationPreferences) {
    return this.request<{ preferences: NotificationPreferences }>('/v1/push/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
  }

  async testPushNotification() {
    return this.request<{ sent: number; total: number }>('/v1/push/test', {
      method: 'POST',
    });
  }

  // Tenant
  async getMe() {
    return this.request<TenantMe>('/v1/tenants/me');
  }

  // Dashboard
  async getDashboardOverview() {
    return this.request<DashboardOverview>('/v1/dashboard/overview');
  }

  // Slash commands (PR4 — Slash Engine Web UI)
  async listSlashCommands(scope = 'dashboard', q?: string) {
    const params = new URLSearchParams({ scope });
    if (q) params.set('q', q);
    return this.request<Array<{
      id: string;
      slug: string;
      name: string;
      description: string | null;
      handler_type: string;
    }>>(`/v1/slash/commands?${params.toString()}`);
  }

  // PR5 — Admin GUI CRUD + audit
  async listSlashCommandsAdmin(filters?: { handler_type?: string; q?: string }) {
    const params = new URLSearchParams();
    if (filters?.handler_type) params.set('handler_type', filters.handler_type);
    if (filters?.q) params.set('q', filters.q);
    const qs = params.toString();
    return this.request<Array<SlashCommandFull>>(`/v1/slash/commands${qs ? `?${qs}` : ''}`);
  }
  async createSlashCommand(payload: SlashCommandCreate) {
    return this.request<SlashCommandFull>('/v1/slash/commands', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
  async getSlashCommand(idOrSlug: string) {
    return this.request<SlashCommandFull>(`/v1/slash/commands/${encodeURIComponent(idOrSlug)}`);
  }
  async updateSlashCommand(idOrSlug: string, patch: Partial<SlashCommandCreate>) {
    return this.request<SlashCommandFull>(`/v1/slash/commands/${encodeURIComponent(idOrSlug)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }
  async deleteSlashCommand(idOrSlug: string) {
    return this.request<{ deleted: boolean }>(`/v1/slash/commands/${encodeURIComponent(idOrSlug)}`, {
      method: 'DELETE',
    });
  }
  async listSlashExecutions(filters?: { slash_command_id?: string; status?: string; channel?: string; from?: string; to?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.slash_command_id) params.set('slash_command_id', filters.slash_command_id);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.channel) params.set('channel', filters.channel);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return this.request<Array<SlashExecutionListItem>>(`/v1/slash/executions${qs ? `?${qs}` : ''}`);
  }
  async getSlashExecution(id: string) {
    return this.request<{ execution: SlashExecutionFull; child_task: Record<string, unknown> | null }>(
      `/v1/slash/executions/${id}`,
    );
  }
  async cancelSlashExecution(id: string) {
    return this.request<{ ok: boolean; status: string }>(
      `/v1/slash/executions/${id}/cancel`,
      { method: 'POST' },
    );
  }

  // Companion
  // companionId é REQUIRED (não opcional). Backend rejeita o request sem ele
  // — sem o param, server caía em fallback `is_default` que gravava mensagens
  // de Mobi como Calebe. Type system força caller a resolver explicitamente.
  async sendMessage(
    input: string,
    channel: string,
    sessionId: string | undefined,
    companionId: string,
    /** Correção UX v2 §5.2.7 — override de provedor/modelo da NOVA sessão
     *  (só tem efeito quando session_id é undefined; backend valida contra
     *  catálogo + conta ativa e persiste em metadata.model_override). */
    modelOverride?: { provider_key?: string; model_key?: string },
  ) {
    return this.request<{
      output: string;
      intent?: string;
      session_id: string;
    }>('/v1/companion/message', {
      method: 'POST',
      body: JSON.stringify({
        input,
        channel,
        session_id: sessionId,
        companion_id: companionId,
        ...(modelOverride?.provider_key ? { provider_key: modelOverride.provider_key } : {}),
        ...(modelOverride?.model_key ? { model_key: modelOverride.model_key } : {}),
      }),
    });
  }

  async sendOnboardingMessage(input: string, sessionId?: string, collectedData?: Record<string, unknown>, conversationHistory?: Array<{ role: string; content: string }>) {
    return this.request<{
      output: string;
      extracted_data?: Record<string, unknown>;
      session_id: string;
      confidence: number;
    }>('/v1/companion/message', {
      method: 'POST',
      body: JSON.stringify({
        input,
        channel: 'dashboard',
        session_id: sessionId,
        mode: 'onboarding',
        onboarding_collected: collectedData ?? {},
        conversation_history: conversationHistory ?? [],
      }),
    });
  }

  async finalizeOnboarding(data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/v1/onboarding/step/2/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Onboarding
  async getOnboardingStatus() {
    return this.request<OnboardingStatus>('/v1/onboarding/status');
  }

  async completeOnboardingStep(step: number, data?: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/v1/onboarding/step/${step}/complete`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  // Visions
  async listVisions() {
    return this.request<Vision[]>('/v1/visions');
  }

  async createVision(data: {
    level: string;
    title: string;
    description?: string;
    target_score?: number;
    business_id?: string;
  }) {
    return this.request<Vision>('/v1/visions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVision(visionId: string, data: Record<string, unknown>) {
    return this.request<Vision>(`/v1/visions/${visionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async scoreVision(visionId: string, data: {
    activity_id: string;
    impact_score: number;
    confidence: number;
    concept_id?: string;
    scored_by?: string;
  }) {
    return this.request<Record<string, unknown>>(`/v1/visions/${visionId}/score`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getVisionScore(visionId: string) {
    return this.request<Record<string, unknown>>(`/v1/visions/${visionId}/score`);
  }

  // Businesses
  async listBusinesses() {
    return this.request<Business[]>('/v1/businesses');
  }

  async createBusiness(data: {
    name: string;
    type: BusinessType;
    subtype?: string;
    revenue_goal?: RevenueGoalType;
    maturity_level?: MaturityLevel;
    dna?: Record<string, unknown>;
  }) {
    return this.request<Business>('/v1/businesses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBusiness(id: string) {
    return this.request<Business>(`/v1/businesses/${id}`);
  }

  /** Reforma UX §7.4 — PATCH genérico de business (nome, tipo, subtipo, logo_url). */
  async updateBusiness(id: string, data: Partial<{
    name: string;
    type: BusinessType;
    subtype: string | null;
    logo_url: string | null;
  }>) {
    return this.request<Business>(`/v1/businesses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async transitionBusinessLifecycle(id: string, lifecycle_status: LifecycleStatus) {
    return this.request<Business>(`/v1/businesses/${id}/lifecycle`, {
      method: 'PATCH',
      body: JSON.stringify({ lifecycle_status }),
    });
  }

  async getBusinessDNA(id: string) {
    return this.request<BusinessDNA>(`/v1/businesses/${id}/dna`);
  }

  async updateBusinessDNA(id: string, data: {
    market?: string | null;
    audience?: string | null;
    revenue_model?: string | null;
    channels?: string[];
    custom_metrics?: string[];
    competitive_landscape?: Record<string, unknown>;
  }) {
    return this.request<BusinessDNA>(`/v1/businesses/${id}/dna`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getBusinessCLevels(id: string) {
    return this.request<CLevel[]>(`/v1/businesses/${id}/c-levels`);
  }

  async listRevenueGoals(businessId: string) {
    return this.request<RevenueGoal[]>(`/v1/businesses/${businessId}/revenue-goals`);
  }

  async createRevenueGoal(businessId: string, data: {
    period: 'monthly' | 'quarterly' | 'yearly';
    period_start: string;
    target_amount: number;
    currency?: string;
    notes?: string;
  }) {
    return this.request<RevenueGoal>(`/v1/businesses/${businessId}/revenue-goals`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Soft delete de negócio (plano §4.3.1) — DELETE /v1/businesses/:id → 200
   * { ok: true }. Erros relevantes pro caller (mensagem do backend no toast):
   * 403 forbidden_not_owner · 409 business_has_demands (mensagem com contagem).
   */
  async deleteBusiness(id: string): Promise<void> {
    return this.request<void>(`/v1/businesses/${id}`, {
      method: 'DELETE',
    });
  }

  // Business Repos
  async listBusinessRepos(businessId: string): Promise<BusinessRepo[]> {
    return this.request<BusinessRepo[]>(`/v1/businesses/${businessId}/repos`);
  }

  async addBusinessRepo(businessId: string, data: AddBusinessRepoParams): Promise<BusinessRepo> {
    return this.request<BusinessRepo>(`/v1/businesses/${businessId}/repos`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBusinessRepo(businessId: string, repoId: string, data: Partial<{ default_branch: string; pr_branch: string; protected_branches: string[]; token: string }>): Promise<BusinessRepo> {
    return this.request<BusinessRepo>(`/v1/businesses/${businessId}/repos/${repoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBusinessRepo(businessId: string, repoId: string): Promise<void> {
    return this.request<void>(`/v1/businesses/${businessId}/repos/${repoId}`, {
      method: 'DELETE',
    });
  }

  // Business Assets (DEM-2123 — Marketing/Branding)
  async listBusinessAssets(businessId: string, kind?: BusinessAssetKind): Promise<BusinessAsset[]> {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
    return this.request<BusinessAsset[]>(`/v1/businesses/${businessId}/assets${qs}`);
  }

  /** Upload multipart de um asset. NÃO seta Content-Type (browser monta o boundary). */
  async uploadBusinessAsset(
    businessId: string,
    file: File,
    opts?: { filename?: string; description?: string; kind?: BusinessAssetKind },
  ): Promise<BusinessAsset> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.filename) form.append('filename', opts.filename);
    if (opts?.description) form.append('description', opts.description);
    if (opts?.kind) form.append('kind', opts.kind);

    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/v1/businesses/${businessId}/assets`, {
      method: 'POST',
      headers,
      body: form,
    });
    const rawText = await res.text();
    let json: ApiResponse<BusinessAsset>;
    try {
      json = rawText ? JSON.parse(rawText) : ({} as ApiResponse<BusinessAsset>);
    } catch {
      const preview = rawText.slice(0, 300).replace(/\s+/g, ' ');
      throw new Error(`Upload: resposta inválida do servidor (HTTP ${res.status}): ${preview}`);
    }
    if (!res.ok || !json.success) {
      if (res.status === 401) {
        this.logout();
        window.location.href = '/login';
        throw new Error('Sessao expirada. Redirecionando para login...');
      }
      throw new Error(json.error?.message ?? `Erro ${res.status} ao subir arquivo.`);
    }
    return json.data;
  }

  async getBusinessAssetDownloadUrl(
    businessId: string,
    assetId: string,
  ): Promise<{ signed_url: string; filename: string }> {
    return this.request<{ signed_url: string; filename: string }>(
      `/v1/businesses/${businessId}/assets/${assetId}/download`,
    );
  }

  async updateBusinessAsset(
    businessId: string,
    assetId: string,
    data: Partial<{ filename: string; description: string | null }>,
  ): Promise<BusinessAsset> {
    return this.request<BusinessAsset>(`/v1/businesses/${businessId}/assets/${assetId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Business Domains (DEM-2121 F5)
  async listBusinessDomains(businessId: string): Promise<BusinessDomain[]> {
    return this.request<BusinessDomain[]>(`/v1/businesses/${businessId}/domains`);
  }

  async addBusinessDomain(
    businessId: string,
    data: { domain: string; is_primary?: boolean },
  ): Promise<BusinessDomain> {
    return this.request<BusinessDomain>(`/v1/businesses/${businessId}/domains`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBusinessDomain(
    businessId: string,
    domainId: string,
    data: { is_primary?: boolean },
  ): Promise<BusinessDomain> {
    return this.request<BusinessDomain>(`/v1/businesses/${businessId}/domains/${domainId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBusinessAsset(businessId: string, assetId: string): Promise<void> {
    return this.request<void>(`/v1/businesses/${businessId}/assets/${assetId}`, {
      method: 'DELETE',
    });
  }

  async verifyBusinessDomain(businessId: string, domainId: string): Promise<DomainVerifyResult> {
    return this.request<DomainVerifyResult>(
      `/v1/businesses/${businessId}/domains/${domainId}/verify`,
      { method: 'POST' },
    );
  }

  async deleteBusinessDomain(businessId: string, domainId: string): Promise<void> {
    return this.request<void>(`/v1/businesses/${businessId}/domains/${domainId}`, {
      method: 'DELETE',
    });
  }

  // Content
  async listContent() {
    return this.request<ContentItem[]>('/v1/content');
  }

  async getContent(id: string) {
    return this.request<ContentItem>(`/v1/content/${id}`);
  }

  async ingestContent(data: { text: string; source: string; channel?: string }) {
    return this.request<ContentItem>('/v1/content/ingest', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async extractContent(id: string) {
    return this.request<ContentItem>(`/v1/content/${id}/extract`, {
      method: 'POST',
    });
  }

  // Persons
  async listPersons() {
    return this.request<Person[]>('/v1/persons');
  }

  async getPerson(id: string) {
    return this.request<PersonDetail>(`/v1/persons/${id}`);
  }

  async createPerson(data: { name: string; email?: string; phone?: string; role?: string; tags?: string[] }) {
    return this.request<Person>('/v1/persons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePerson(id: string, data: Record<string, unknown>) {
    return this.request<Person>(`/v1/persons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePerson(id: string) {
    return this.request<void>(`/v1/persons/${id}`, { method: 'DELETE' });
  }

  async listPersonRelationships(id: string) {
    return this.request<PersonRelationship[]>(`/v1/persons/${id}/relationships`);
  }

  async createPersonRelationship(id: string, data: { related_person_id: string; relationship_type: string; context?: string }) {
    return this.request<PersonRelationship>(`/v1/persons/${id}/relationships`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listPersonEvidences(id: string) {
    return this.request<PersonEvidence[]>(`/v1/persons/${id}/evidences`);
  }

  async createPersonEvidence(id: string, data: { description: string; evidence_type: string; confidence: number }) {
    return this.request<PersonEvidence>(`/v1/persons/${id}/evidences`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listPersonInferences(id: string) {
    return this.request<PersonInference[]>(`/v1/persons/${id}/inferences`);
  }

  async createPersonInference(id: string, data: { statement: string; confidence: number; basis?: string }) {
    return this.request<PersonInference>(`/v1/persons/${id}/inferences`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Pull Requests
  async listPullRequests() {
    return this.request<PullRequest[]>('/v1/pull-requests');
  }

  async createPullRequest(data: { repo: string; branch_source: string; branch_target: string; task_id?: string; author_agent: string }) {
    return this.request<PullRequest>('/v1/pull-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPullRequestsByTask(taskId: string) {
    return this.request<PullRequest[]>(`/v1/pull-requests/by-task/${taskId}`);
  }

  // DEM-2037 — PRs linkados a uma demanda (aba PRs do DemandDetailPanel).
  // Lê pull_requests filtrado por demand_id no backend (rota nova), via api.ts
  // — sem supabase.from() direto no front (AC#4). Vazio = [].
  async getDemandPullRequests(demandId: string) {
    return this.request<PullRequest[]>(`/v1/demands/${demandId}/pull_requests`);
  }

  // DEM-2037 — artefatos de entrega de uma demanda (aba Artefatos + viewer
  // fullscreen). Backend deriva url/extension/mime pro viewer multi-extensão.
  async getDemandArtifacts(demandId: string) {
    return this.request<DemandArtifact[]>(`/v1/demands/${demandId}/artifacts`);
  }

  async transitionPullRequest(id: string, newStatus: PRStatus, reviewerAgent?: string) {
    return this.request<PullRequest>(`/v1/pull-requests/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: newStatus, reviewer_agent: reviewerAgent }),
    });
  }

  // Personal
  async getPersonalDNA() {
    return this.request<PersonalDNA>('/v1/personal/dna');
  }

  async updatePersonalDNA(data: Record<string, unknown>) {
    return this.request<PersonalDNA>('/v1/personal/dna', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async listLifeAreas() {
    return this.request<LifeArea[]>('/v1/personal/life-areas');
  }

  async createLifeArea(data: { name: string; description?: string; sort_order?: number; is_active?: boolean }) {
    return this.request<LifeArea>('/v1/personal/life-areas', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async seedLifeAreas() {
    return this.request<LifeArea[]>('/v1/personal/life-areas/seed', {
      method: 'POST',
    });
  }

  async createDailyPriority(data: {
    date: string;
    daily_priority: string;
    daily_priority_pillar: string;
    t2_items?: string[];
    t3_items?: string[];
  }) {
    return this.request<DailyPriority>('/v1/personal/daily-priority', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listDailyPriorities() {
    return this.request<DailyPriority[]>('/v1/personal/daily-priority');
  }

  async updateDailyPriority(id: string, data: { daily_priority?: string; daily_priority_pillar?: string; t2_items?: string[]; t3_items?: string[]; completed?: boolean; reflection?: string }) {
    return this.request<DailyPriority>(`/v1/personal/daily-priority/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createTracking(data: { indicator_id: string; value: number; recorded_at: string; notes?: string }) {
    return this.request<TrackingEntry>('/v1/personal/tracking', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async listTracking() {
    return this.request<TrackingEntry[]>('/v1/personal/tracking');
  }

  // Sessions
  async createSession(channel: string) {
    return this.request<Session>('/v1/sessions', {
      method: 'POST',
      body: JSON.stringify({ channel }),
    });
  }

  async listSessions(params?: { page?: number; per_page?: number; status?: string; channel?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.per_page) searchParams.set('per_page', String(params.per_page));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.channel) searchParams.set('channel', params.channel);
    const qs = searchParams.toString();
    const data = await this.request<{ sessions: Session[] } | Session[]>(`/v1/sessions${qs ? `?${qs}` : ''}`);
    // Backend returns { sessions: [...] } or directly [...]
    return Array.isArray(data) ? data : (data as { sessions: Session[] }).sessions ?? [];
  }

  async getSession(id: string) {
    return this.request<Session>(`/v1/sessions/${id}`);
  }

  async endSession(id: string) {
    return this.request<Session>(`/v1/sessions/${id}/end`, {
      method: 'POST',
    });
  }

  /** Reforma UX §7.1 — renomeia a sessão (persiste companion_sessions.title). */
  async renameSession(id: string, title: string) {
    const data = await this.request<{ session: Session }>(`/v1/sessions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
    return data.session;
  }

  // Daily Priority (fast — Onda 2)
  async getDailyPriorityFast() {
    return this.request<DailyPriorityFast>('/v1/daily-priority');
  }

  async setDailyPriorityFast(data: { daily_priority: string; daily_priority_pillar?: string; t2_items?: string[]; t3_items?: string[] }) {
    return this.request<DailyPriorityFast>('/v1/daily-priority', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Orchestrate
  async orchestrate(data: { scope: string; agent_ids: string[]; sizing?: 'CURTA' | 'MEDIA' | 'LONGA' }) {
    return this.request<OrchestrateResult>('/v1/engine/orchestrate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // LLM OAuth
  async llmAuthorize(reconnectAccountId?: string): Promise<{ redirectUrl: string; state: string }> {
    const qs = reconnectAccountId ? `?reconnect_account_id=${encodeURIComponent(reconnectAccountId)}` : '';
    return this.request<{ redirectUrl: string; state: string }>(`/v1/auth/llm/authorize${qs}`);
  }

  async llmExchange(code: string, state: string) {
    return this.request<{ tenantId: string; accountId: string; provider: string }>('/v1/auth/llm/exchange', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async llmStatus() {
    return this.request<LlmStatusResponse>('/v1/auth/llm/status');
  }

  // ─── DEM-04.5 OpenAI OAuth (Device Code Flow — Sign in with ChatGPT) ──

  async openaiOAuthStatus(): Promise<{
    configured: boolean;
    flow: 'device_code';
    issuer: string;
  }> {
    return this.request('/v1/auth/openai/status');
  }

  /** Inicia device code flow. Retorna user_code pra mostrar pro usuário. */
  async openaiDeviceStart(reconnectAccountId?: string): Promise<{
    state: string;
    user_code: string;
    verification_url: string;
    interval: number;
    expires_in: number;
  }> {
    return this.request('/v1/auth/openai/device/start', {
      method: 'POST',
      body: JSON.stringify(reconnectAccountId ? { reconnect_account_id: reconnectAccountId } : {}),
    });
  }

  /** Polla 1x. Retorna pending|success|expired|error. Frontend chama em loop. */
  async openaiDevicePoll(state: string): Promise<{
    status: 'pending' | 'success' | 'expired' | 'error';
    account_id?: string;
    error?: string;
  }> {
    return this.request('/v1/auth/openai/device/poll', {
      method: 'POST',
      body: JSON.stringify({ state }),
    });
  }

  // ─── DEM-2170 Grok OAuth (xAI Device Code Flow — auth.x.ai) ──────────
  // Backend vivo na main (DEM-2156 + DEM-2166): rotas GET /v1/auth/grok/status,
  // POST /v1/auth/grok/device/start e POST /v1/auth/grok/device/poll. Mesmo
  // contrato device-code do OpenAI. A UI de API key permanece backend-only
  // (ADR-2026-07-18-OAUTH-FIRST) — não há método api-key aqui de propósito.

  async grokOAuthStatus(): Promise<{
    configured: boolean;
    flow: 'device_code';
    issuer: string;
  }> {
    return this.request('/v1/auth/grok/status');
  }

  /** Inicia device code flow da xAI. Retorna user_code pra mostrar pro usuário. */
  async grokDeviceStart(reconnectAccountId?: string): Promise<{
    state: string;
    user_code: string;
    verification_url: string;
    interval: number;
    expires_in: number;
  }> {
    return this.request('/v1/auth/grok/device/start', {
      method: 'POST',
      body: JSON.stringify(reconnectAccountId ? { reconnect_account_id: reconnectAccountId } : {}),
    });
  }

  /** Polla 1x. Retorna pending|success|expired|error. Frontend chama em loop. */
  async grokDevicePoll(state: string): Promise<{
    status: 'pending' | 'success' | 'expired' | 'error';
    account_id?: string;
    error?: string;
  }> {
    return this.request('/v1/auth/grok/device/poll', {
      method: 'POST',
      body: JSON.stringify({ state }),
    });
  }

  // LLM Accounts (with connection_status)
  async listLlmAccountsWithStatus(opts?: { refresh?: boolean }): Promise<LlmAccountWithStatus[]> {
    // refresh=1 (botão "Atualizar limites"): re-mede a quota 5h/7d de todas as
    // contas ANTES de listar. Reusa ESTA rota — já conhecida pelo cliente e que
    // funciona — em vez de um POST novo, que dava 404 por Service Worker/cache
    // preso no navegador. Uma chamada só (mede + devolve a lista atualizada).
    const qs = opts?.refresh ? '?refresh=1' : '';
    const data = await this.request<{ accounts: LlmAccountWithStatus[] }>(`/v1/auth/llm/accounts${qs}`);
    return data.accounts;
  }

  // LLM Accounts (legacy)
  async listLlmAccounts() {
    return this.request<LlmAccount[]>('/v1/llm-accounts');
  }

  async disconnectLlmAccount(id: string) {
    return this.request<void>(`/v1/llm-accounts/${id}`, { method: 'DELETE' });
  }

  /** Define o modelo padrão de uma CONTA LLM (renomeado de updateLlmModel —
   *  colidia com o CRUD de catálogo §7.3). */
  async updateLlmAccountModel(id: string, modelId: string) {
    return this.request<{ account_id: string; model_id: string }>(`/v1/llm-accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ model_id: modelId }),
    });
  }

  /** Atualiza somente o fallback account-scoped; null remove explicitamente. */
  async updateLlmAccountFallbackModel(id: string, fallbackModelId: string | null) {
    return this.request<{ account_id: string; fallback_model_id: string | null }>(`/v1/llm-accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ fallback_model_id: fallbackModelId }),
    });
  }

  async getLlmResilienceAttempts(filters: LlmResilienceFilters = {}): Promise<LlmResilienceAttemptsResponse> {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    }
    return this.request<LlmResilienceAttemptsResponse>(`/v1/llm-resilience/attempts${qs.size ? `?${qs}` : ''}`);
  }

  async getLlmResilienceSummary(params: { since?: string } = {}): Promise<LlmResilienceSummary> {
    const qs = new URLSearchParams();
    if (params.since) qs.set('since', params.since);
    return this.request<LlmResilienceSummary>(`/v1/llm-resilience/summary${qs.size ? `?${qs}` : ''}`);
  }

  async getAdminLlmResilience(filters: AdminLlmResilienceFilters): Promise<LlmResilienceAttemptsResponse> {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== '') qs.set(key, String(value));
    }
    return this.request<LlmResilienceAttemptsResponse>(`/v1/admin/llm-resilience?${qs}`);
  }

  // ─── Multi-provider v5 ────────────────────────────────────────────────

  async connectLlmAccountApiKey(payload: {
    provider: 'anthropic' | 'openai';
    api_key: string;
    model_id?: string;
    display_name?: string;
    label?: string;
  }): Promise<LlmAccount> {
    return this.request<LlmAccount>('/v1/llm-accounts', {
      method: 'POST',
      body: JSON.stringify({
        provider: payload.provider,
        access_token: payload.api_key,
        model_id: payload.model_id,
        display_name: payload.display_name,
        label: payload.label ?? 'api_key',
      }),
    });
  }


  // ─── DEM-2550 Command Control Plane ──────────────────────────────────────
  async controlPlaneProviders() {
    return this.request<{
      providers: Array<{
        key: string;
        display_name: string;
        accounts: Array<{ id: string; provider: string; auth_method: string; label: string; status: string | null }>;
      }>;
      message?: string;
    }>('/v1/control-plane/providers');
  }

  async controlPlaneModels(provider: string, modality = 'text') {
    const qs = new URLSearchParams({ provider, modality });
    return this.request<{
      models: Array<{
        model_key: string;
        display_name: string;
        provider_key: string;
        tier: string | null;
        status: string;
        supported_effort_levels: CanonicalEffort[];
        default_effort_level: CanonicalEffort | null;
        compatible_auth_methods: string[];
      }>;
      message?: string;
      unavailable_reason?: string | null;
    }>(`/v1/control-plane/models?${qs.toString()}`);
  }

  async controlPlaneState(params?: { session_id?: string; companion_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.session_id) qs.set('session_id', params.session_id);
    if (params?.companion_id) qs.set('companion_id', params.companion_id);
    const q = qs.toString();
    return this.request<{
      state: {
        session_id: string | null;
        companion_id: string | null;
        effective: {
          provider: string | null;
          model_key: string | null;
          model_display_name: string | null;
          effort: CanonicalEffort | null;
          reasoning: 'off' | 'on' | 'auto' | null;
          account_id: string | null;
          account_label: string | null;
          auth_method: string | null;
        };
        permanent: {
          provider: string | null;
          model_key: string | null;
          effort: CanonicalEffort | null;
          reasoning: 'off' | 'on' | 'auto' | null;
        };
        session_override: {
          provider: string | null;
          model_key: string | null;
          effort: CanonicalEffort | null;
          reasoning: 'off' | 'on' | 'auto' | null;
        } | null;
        capabilities: {
          effort_levels: CanonicalEffort[];
          reasoning_modes: Array<'off' | 'on' | 'auto'>;
          reasoning_supported: boolean;
        };
        degradation_reason: string | null;
      };
    }>(`/v1/control-plane/state${q ? `?${q}` : ''}`);
  }

  async controlPlaneEffortLevels(params?: { session_id?: string; companion_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.session_id) qs.set('session_id', params.session_id);
    if (params?.companion_id) qs.set('companion_id', params.companion_id);
    const q = qs.toString();
    return this.request<{
      effort_levels: CanonicalEffort[];
      message?: string;
      unavailable_reason?: string | null;
    }>(`/v1/control-plane/effort-levels${q ? `?${q}` : ''}`);
  }

  async controlPlaneReasoningModes(params?: { session_id?: string; companion_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.session_id) qs.set('session_id', params.session_id);
    if (params?.companion_id) qs.set('companion_id', params.companion_id);
    const q = qs.toString();
    return this.request<{
      reasoning_modes: Array<'off' | 'on' | 'auto'>;
      message?: string;
      unavailable_reason?: string | null;
    }>(`/v1/control-plane/reasoning-modes${q ? `?${q}` : ''}`);
  }

  async controlPlaneCommands(channel = 'dashboard') {
    return this.request<{
      commands: Array<{ slug: string; name: string; description: string | null }>;
      message?: string;
    }>(`/v1/control-plane/commands?channel=${encodeURIComponent(channel)}`);
  }

  async controlPlaneApply(payload: {
    companion_id: string;
    session_id?: string | null;
    provider?: string;
    model_key?: string;
    effort?: CanonicalEffort;
    reasoning?: 'off' | 'on' | 'auto';
    scope: 'session' | 'permanent';
  }) {
    return this.request<{
      message?: string;
      state?: unknown;
      audit?: unknown;
    }>('/v1/control-plane/apply', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listLlmProviders(): Promise<LlmProvider[]> {
    const data = await this.request<{ providers: LlmProvider[] }>('/v1/llm-providers');
    return data.providers ?? [];
  }

  async listLlmModels(filters?: {
    provider?: string;
    modality?: Modality;
    tier?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ models: LlmModel[]; page: number; page_size: number; total: number }> {
    const qs = new URLSearchParams();
    if (filters?.provider) qs.set('provider', filters.provider);
    if (filters?.modality) qs.set('modality', filters.modality);
    if (filters?.tier) qs.set('tier', filters.tier);
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.page) qs.set('page', String(filters.page));
    if (filters?.page_size) qs.set('page_size', String(filters.page_size));
    const path = `/v1/llm-models${qs.toString() ? '?' + qs.toString() : ''}`;
    return this.request<{ models: LlmModel[]; page: number; page_size: number; total: number }>(path);
  }

  // ─── Reforma UX §7.3 — CRUD admin de modelos LLM ───────────────────────────

  async createLlmModel(payload: Partial<LlmModel> & {
    provider_id: string;
    model_key: string;
    display_name: string;
    input_cost_per_m: number;
    output_cost_per_m: number;
  }): Promise<LlmModel> {
    const data = await this.request<{ model: LlmModel }>('/v1/llm-models', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data.model;
  }

  async updateLlmModel(id: string, payload: Partial<LlmModel>): Promise<LlmModel> {
    const data = await this.request<{ model: LlmModel }>(`/v1/llm-models/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data.model;
  }

  /** Soft-delete: backend marca status='deprecated' (row preservada). */
  async deleteLlmModel(id: string): Promise<LlmModel> {
    const data = await this.request<{ model: LlmModel }>(`/v1/llm-models/${id}`, {
      method: 'DELETE',
    });
    return data.model;
  }

  async listAgentInstances(): Promise<AgentInstance[]> {
    const data = await this.request<{ instances: AgentInstance[] }>('/v1/agent-instances');
    return data.instances ?? [];
  }

  async createAgentInstance(payload: {
    agent_id: string;
    display_name?: string;
    config_overrides?: AgentInstance['config_overrides'];
    user_md_override?: string;
  }): Promise<AgentInstance> {
    const data = await this.request<{ instance: AgentInstance }>('/v1/agent-instances', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return data.instance;
  }

  async updateAgentInstance(
    id: string,
    payload: Partial<{
      display_name: string;
      config_overrides: AgentInstance['config_overrides'];
      state: AgentInstance['state'];
      user_md_override: string;
    }>,
  ): Promise<AgentInstance> {
    const data = await this.request<{ instance: AgentInstance }>(`/v1/agent-instances/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    return data.instance;
  }

  async deleteAgentInstance(id: string): Promise<void> {
    await this.request<{ deleted: boolean }>(`/v1/agent-instances/${id}`, { method: 'DELETE' });
  }

  async resolveWizardUserMd(): Promise<WizardResolveResponse> {
    return this.request<WizardResolveResponse>('/v1/agent-instances/wizard/resolve', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async spawnEphemeral(payload: {
    provider: 'anthropic' | 'openai';
    model_key: string;
    modality?: Modality;
    reasoning_effort?: CanonicalEffort;
    input: { prompt?: string; messages?: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> };
    timeout_ms?: number;
  }): Promise<{
    execution_id: string;
    output: string;
    model_used: string;
    tokens_input: number;
    tokens_output: number;
    duration_ms: number;
    cost_usd: number;
  }> {
    return this.request('/v1/ephemeral/spawn', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listEphemeralExecutions(): Promise<EphemeralExecution[]> {
    const data = await this.request<{ executions: EphemeralExecution[] }>('/v1/ephemeral/executions');
    return data.executions ?? [];
  }

  // Telegram Integration
  async telegramSetup(botToken: string) {
    return this.request<{ bot_username: string; connected: boolean; webhook_configured: boolean }>('/v1/integrations/telegram/setup', {
      method: 'POST',
      body: JSON.stringify({ bot_token: botToken }),
    });
  }

  async telegramConnect() {
    return this.request<TelegramConnectResponse>('/v1/integrations/telegram/connect', {
      method: 'POST',
    });
  }

  async telegramStatus() {
    return this.request<TelegramStatusResponse>('/v1/integrations/telegram/status');
  }

  async telegramDisconnect() {
    return this.request<void>('/v1/integrations/telegram', { method: 'DELETE' });
  }

  // Tasks
  async listTasks(filters?: {
    status?: string;
    handler?: string;
    parent_task_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<TasksListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.handler) params.set('handler', filters.handler);
    if (filters?.parent_task_id) params.set('parent_task_id', filters.parent_task_id);
    if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return this.request<TasksListResponse>(`/v1/tasks${qs ? '?' + qs : ''}`);
  }

  /** GET /v1/tasks/stats — counts por status + avg_duration por handler (Kanban perf). */
  async getTaskStats(): Promise<TasksStatsResponse> {
    return this.request<TasksStatsResponse>('/v1/tasks/stats');
  }

  /** GET /v1/tasks/:id — task completa com input/output/error jsonb (detail modal). */
  async getTask(taskId: string): Promise<BackendTask> {
    return this.request<BackendTask>(`/v1/tasks/${taskId}`);
  }

  async transitionTask(taskId: string, status: string): Promise<BackendTask> {
    return this.request<BackendTask>(`/v1/tasks/${taskId}/transition`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // Projects
  async listProjects(): Promise<ProjectSummary[]> {
    const result = await this.request<{ data: ProjectSummary[] } | ProjectSummary[]>('/v1/projects?per_page=100');
    return Array.isArray(result) ? result : (result.data ?? []);
  }

  // Demands
  /**
   * Correção UX v2 W1.5 — execuções agregadas da demanda server-side
   * (substitui as queries supabase.from() diretas do DemandDetailPanel,
   * que sofriam cap de 1000 rows do PostgREST).
   */
  async getDemandExecutions(id: string, opts?: { limit?: number; offset?: number }): Promise<{
    demand_id: string;
    tasks: Array<{
      id: string; title: string | null; status: string; handler?: string | null;
      step_slug?: string | null; step_number?: number | null; created_at: string;
    } & Record<string, unknown>>;
    executions: Array<{
      id: string; kind: 'handler' | 'agent' | 'skill'; status: string;
      started_at: string | null; finished_at: string | null; duration_ms: number | null;
      cost_usd: number | null; tokens_input: number | null; tokens_output: number | null;
      error: string | null; handler_name: string | null; skill_id: string | null;
      agent_id: string | null; task_id: string | null; trace_id: string | null;
    }>;
    // DEM-2079 (#3): motivo REAL do bloqueio (execution_state.blocked_reason),
    // interpretado server-side. null quando não bloqueada ou sem motivo
    // estruturado. `raw` alimenta o detalhe técnico colapsável da UI.
    blocked?: DemandBlockedInfo | null;
    pagination: { limit: number; offset: number; tasks_count: number; executions_count: number };
  }> {
    const qs = new URLSearchParams();
    if (opts?.limit) qs.set('limit', String(opts.limit));
    if (opts?.offset) qs.set('offset', String(opts.offset));
    return this.request(`/v1/demands/${id}/executions${qs.toString() ? '?' + qs : ''}`);
  }

  async listDemands(filters?: DemandFilters): Promise<DemandListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.priority) params.set('priority', filters.priority);
    if (filters?.project_id) params.set('project_id', filters.project_id);
    if (filters?.runtime_target) params.set('runtime_target', filters.runtime_target);
    if (filters?.business_id) params.set('business_id', filters.business_id);
    // DEM-2162 — filtro por dono (mine | <tenant_id> | all).
    if (filters?.owner) params.set('owner', filters.owner);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.per_page) params.set('per_page', String(filters.per_page));
    const qs = params.toString();
    return this.request<DemandListResponse>(`/v1/demands${qs ? '?' + qs : ''}`);
  }

  async getDemand(id: string): Promise<Demand> {
    return this.request<Demand>(`/v1/demands/${id}`);
  }

  async createDemand(data: {
    title: string;
    description: string;
    priority?: DemandPriority;
    type?: string;
    project_id?: string;
    origin_type?: string;
    creator_type?: string;
    pipeline_slug?: string;
    pipeline_version_id?: string;
    business_id?: string;
    // PR11 plano pipeline-canonical-rebuild: texto OBRIGATORIO descrevendo
    // gatilho/motivo da demand a partir da UI. Convertido em
    // metadata.creator_context canonical { origin: 'ui', creator_context } pro POST.
    creator_context: string;
  }): Promise<Demand> {
    const { creator_context, ...rest } = data;
    return this.request<Demand>('/v1/demands', {
      method: 'POST',
      body: JSON.stringify({
        ...rest,
        origin_type: rest.origin_type ?? 'manual',
        creator_type: rest.creator_type ?? 'human',
        // PR11: metadata.creator_context shape canonical (docs/specs/pipeline.md §7).
        // CHECK constraint demands_creator_context_canonical (migration PR2) exige
        // origin in (companion|ui|cron) + creator_context texto >=1 char.
        metadata: {
          creator_context: {
            origin: 'ui' as const,
            creator_context,
          },
        },
      }),
    });
  }

  /**
   * Reforma UX (DASH-07): contrato novo do backend aceita `feedback` opcional
   * no PATCH /v1/demands/:id/transition — { notes?, return_step?, resubmit? }
   * persiste em metadata.rejection_feedback. Backend ANTIGO (pré-restart) usa
   * Zod estrito e responde 4xx quando o campo extra vem no body — caller deve
   * degradar (re-tentar sem feedback) se precisar de compat.
   */
  async transitionDemand(
    id: string,
    status: string,
    feedback?: DemandTransitionFeedback,
  ): Promise<Demand> {
    return this.request<Demand>(`/v1/demands/${id}/transition`, {
      method: 'PATCH',
      body: JSON.stringify({ status, ...(feedback ? { feedback } : {}) }),
    });
  }

  async getTrace(traceId: string): Promise<TraceChain> {
    return this.request<TraceChain>(`/v1/trace/${traceId}`);
  }

  // PR3 — demand progress endpoint
  async getDemandProgress(id: string): Promise<DemandProgress> {
    return this.request<DemandProgress>(`/v1/demands/${id}/progress`);
  }

  // PR1 — handlers admin (read-only no MVP; CRUD via service_role direto)
  // Backend devolve { handlers, total } — desestrutura aqui pra manter contrato
  // antigo (Handler[]) consumido por AdminHandlersPage.
  // D8 (2026-05-04) — admin wizards endpoints
  async listAdminWizards(includeInactive = false): Promise<AdminWizard[]> {
    const qs = includeInactive ? '?include_inactive=true' : '';
    const data = await this.request<
      { wizards: AdminWizard[]; total: number } | AdminWizard[]
    >(`/v1/admin/wizards${qs}`);
    return Array.isArray(data) ? data : (data.wizards ?? []);
  }

  async getAdminWizard(slug: string): Promise<AdminWizard> {
    return this.request<AdminWizard>(`/v1/admin/wizards/${encodeURIComponent(slug)}`);
  }

  // D4 (2026-05-04) — admin tools endpoints
  async listAdminTools(filters?: {
    category?: string;
    scope?: string;
    requires_admin?: boolean;
    include_inactive?: boolean;
  }): Promise<AdminTool[]> {
    const qs = new URLSearchParams();
    if (filters?.category) qs.set('category', filters.category);
    if (filters?.scope) qs.set('scope', filters.scope);
    if (filters?.requires_admin) qs.set('requires_admin', 'true');
    if (filters?.include_inactive) qs.set('include_inactive', 'true');
    const q = qs.toString();
    const data = await this.request<{ tools: AdminTool[] } | AdminTool[]>(
      `/v1/admin/tools${q ? '?' + q : ''}`,
    );
    return Array.isArray(data) ? data : ((data as { tools?: AdminTool[] }).tools ?? []);
  }

  async getAdminTool(slug: string): Promise<AdminTool> {
    return this.request<AdminTool>(`/v1/admin/tools/${encodeURIComponent(slug)}`);
  }

  async listAdminAgentTools(filters?: {
    agent_id?: string;
    tool_slug?: string;
    limit?: number;
  }): Promise<AdminAgentTool[]> {
    const qs = new URLSearchParams();
    if (filters?.agent_id) qs.set('agent_id', filters.agent_id);
    if (filters?.tool_slug) qs.set('tool_slug', filters.tool_slug);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    const q = qs.toString();
    const data = await this.request<{ data: AdminAgentTool[] } | AdminAgentTool[]>(
      `/v1/admin/agent-tools${q ? '?' + q : ''}`,
    );
    return Array.isArray(data) ? data : ((data as { data?: AdminAgentTool[] }).data ?? []);
  }

  /** Toggla flags de governança de uma tool (admin-only). is_core=true aplica a
   *  tool a todos os agentes ativos no backend. */
  async patchAdminToolFlags(
    slug: string,
    flags: { requires_admin?: boolean; mcp_exposed?: boolean; is_core?: boolean; is_companion_only?: boolean; is_active?: boolean },
  ): Promise<{ slug: string; requires_admin: boolean; mcp_exposed: boolean; is_core: boolean; is_companion_only?: boolean; is_active?: boolean; canonical_applied_to_agents?: number }> {
    return this.request(`/v1/admin/tools/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body: JSON.stringify(flags),
    });
  }

  /** Atribui (enabled=true) ou remove (enabled=false) uma tool de um agente. */
  async patchAdminAgentTool(
    agentRef: string,
    toolSlug: string,
    enabled: boolean,
  ): Promise<{ agent_id: string; tool_slug: string; enabled: boolean }> {
    return this.request(`/v1/admin/agent-tools`, {
      method: 'PATCH',
      body: JSON.stringify({ agent_id: agentRef, tool_slug: toolSlug, enabled }),
    });
  }

  async listAdminToolExecutions(filters?: {
    tool_slug?: string;
    agent_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminToolExecution[]> {
    const qs = new URLSearchParams();
    if (filters?.tool_slug) qs.set('tool_slug', filters.tool_slug);
    if (filters?.agent_id) qs.set('agent_id', filters.agent_id);
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    if (filters?.offset) qs.set('offset', String(filters.offset));
    const q = qs.toString();
    const data = await this.request<
      { data: AdminToolExecution[] } | AdminToolExecution[]
    >(`/v1/admin/tool-executions${q ? '?' + q : ''}`);
    return Array.isArray(data) ? data : ((data as { data?: AdminToolExecution[] }).data ?? []);
  }

  async listAdminWizardExecutions(filters?: {
    wizard_id?: string;
    wizard_slug?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminWizardExecution[]> {
    const qs = new URLSearchParams();
    if (filters?.wizard_id) qs.set('wizard_id', filters.wizard_id);
    if (filters?.wizard_slug) qs.set('wizard_slug', filters.wizard_slug);
    if (filters?.status) qs.set('status', filters.status);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    if (filters?.offset) qs.set('offset', String(filters.offset));
    const q = qs.toString();
    const data = await this.request<
      | { executions: AdminWizardExecution[]; total: number; limit: number; offset: number }
      | AdminWizardExecution[]
    >(`/v1/admin/wizard-executions${q ? '?' + q : ''}`);
    return Array.isArray(data) ? data : (data.executions ?? []);
  }

  async listHandlers(): Promise<Handler[]> {
    const data = await this.request<{ handlers: Handler[]; total: number } | Handler[]>(
      '/v1/handlers',
    );
    return Array.isArray(data) ? data : (data.handlers ?? []);
  }

  async getHandlerByName(name: string): Promise<Handler> {
    return this.request<Handler>(`/v1/handlers/${encodeURIComponent(name)}`);
  }

  async listHandlerExecutions(filters?: {
    handler_id?: string;
    demand_id?: string;
    task_id?: string;
    origin?: string;
    limit?: number;
    offset?: number;
  }): Promise<HandlerExecution[]> {
    const qs = new URLSearchParams();
    if (filters?.handler_id) qs.set('handler_id', filters.handler_id);
    if (filters?.demand_id) qs.set('demand_id', filters.demand_id);
    if (filters?.task_id) qs.set('task_id', filters.task_id);
    if (filters?.origin) qs.set('origin', filters.origin);
    if (filters?.limit) qs.set('limit', String(filters.limit));
    if (filters?.offset) qs.set('offset', String(filters.offset));
    const q = qs.toString();
    const data = await this.request<
      | { executions: HandlerExecution[]; total: number; limit: number; offset: number }
      | HandlerExecution[]
    >(`/v1/handler-executions${q ? '?' + q : ''}`);
    return Array.isArray(data) ? data : (data.executions ?? []);
  }

  async getHandlerExecution(id: string): Promise<HandlerExecution> {
    return this.request<HandlerExecution>(`/v1/handler-executions/${id}`);
  }

  // PR1 — routes admin
  async listRoutes(): Promise<RouteRow[]> {
    const data = await this.request<{ routes: RouteRow[]; total: number } | RouteRow[]>(
      '/v1/routes',
    );
    return Array.isArray(data) ? data : (data.routes ?? []);
  }

  // F6.1 (2026-05-22) — Wizard schema dinâmico (D3 endpoint, backend PR #510).
  // GET /v1/wizards/:slug/schema retorna fields derivados de entity_contracts
  // (create+update). Frontend renderiza form dinâmico — zero hardcoded.
  async getWizardSchema(slug: string): Promise<WizardSchemaResponse> {
    return this.request<WizardSchemaResponse>(`/v1/wizards/${encodeURIComponent(slug)}/schema`);
  }

  // F6.1 (2026-05-22) — submit do form dinâmico do wizard.
  // POST /v1/wizards/:slug/submit com payload {fields: {...}} → backend valida
  // contra entity_contracts e cria artifact. Quando backend retorna 422 com
  // code=contract_validation_failed + field, frontend destaca campo problemático.
  async submitWizardForm(slug: string, fields: Record<string, unknown>): Promise<WizardSubmitResponse> {
    return this.request<WizardSubmitResponse>(`/v1/wizards/${encodeURIComponent(slug)}/submit`, {
      method: 'POST',
      body: JSON.stringify({ fields }),
    });
  }

  // F-FINAL-38 (2026-05-25): endpoints `available` consumidos pelo
  // DynamicWizardPage MultiSelectLookup quando WizardField tem
  // lookup_endpoint. Path passa via fetchLookup(endpoint).
  async fetchLookup<T>(endpoint: string): Promise<T> {
    if (!endpoint.startsWith('/')) throw new Error(`lookup_endpoint inválido: ${endpoint}`);
    return this.request<T>(endpoint);
  }

  // PR7 — wizards
  // wizard-create-agent-fix (2026-05-11): response inclui opening_message
  // renderizada do template (com {admin_name} substituído). Frontend usa
  // pra mostrar saudação proativa sem precisar de input do user.
  async startWizard(slug: string, body: { channel?: string; channel_user_id?: string; session_token?: string }): Promise<{ execution: WizardExecution; demand_id: string | null; opening_message: string | null }> {
    return this.request<{ execution: WizardExecution; demand_id: string | null; opening_message: string | null }>(`/v1/wizards/${slug}/start`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async pauseWizard(executionId: string, reason?: string): Promise<WizardExecution> {
    return this.request<WizardExecution>(`/v1/wizards/executions/${executionId}/pause`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async resumeWizard(executionId: string): Promise<WizardExecution> {
    return this.request<WizardExecution>(`/v1/wizards/executions/${executionId}/resume`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async completeWizard(
    executionId: string,
    body: { artifact_id: string },
  ): Promise<WizardExecution> {
    return this.request<WizardExecution>(`/v1/wizards/executions/${executionId}/complete`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async abandonWizard(executionId: string, reason?: string): Promise<WizardExecution> {
    return this.request<WizardExecution>(`/v1/wizards/executions/${executionId}/abandon`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * PR7-fix-B (2026-05-02): turn de wizard via /v1/wizards/executions/:id/turn.
   * Substitui agentWizardApi.turn (DELETADA) e api.dispatchSkillBuilder (DELETADA).
   * Backend dispatch via @wizard-runner state-backed → conductor (agent-builder ou skill-builder).
   */
  async wizardTurn(
    executionId: string,
    user_message: string,
    opts?: { attachments?: WizardAttachment[] },
  ): Promise<{
    wizard_execution_id: string;
    wizard_slug: string;
    conductor: string;
    current_wave?: 1 | 2 | 3 | 4;
    is_complete?: boolean;
    collected?: Record<string, unknown>;
    assistant_message?: string;
    [key: string]: unknown;
  }> {
    // F-FINAL-69: `attachments` opcional. Backend (wizard-runner) lê quando
    // presente e propaga pro conductor — preview do arquivo na bolha de chat
    // permanece (UX intacta). Quando vazio, body fica igual ao legacy.
    const body: Record<string, unknown> = { user_message };
    if (opts?.attachments && opts.attachments.length > 0) {
      body.attachments = opts.attachments;
    }
    return this.request(`/v1/wizards/executions/${executionId}/turn`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * PR7-fix-B (2026-05-02): publish-agent via /v1/wizards/executions/:id/publish-agent.
   * Substitui agentWizardApi.publish (DELETADA). Lê wizard_executions.state.collected,
   * cria companion_profile (RPC) ou agent (INSERT), dispatch wizard-complete.
   */
  async publishAgentWizard(executionId: string): Promise<{
    success: boolean;
    entity_type: 'companion_profile' | 'agent';
    entity_id: string;
    slug: string;
    redirect_url: string;
  }> {
    return this.request(`/v1/wizards/executions/${executionId}/publish-agent`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /**
   * F-FINAL-61 (2026-05-26): publish-skill via /v1/wizards/executions/:id/publish-skill.
   * Substitui publishSkillDraft (route legada /skill-drafts/:id/publish que lia
   * skill_drafts.payload.slug — wizard conversational não popula skill_drafts).
   * Esta route lê wizard_executions.state.collected (canonical pós F-FINAL-57a),
   * dispara ensureSkillStorage + upload scripts/refs (F-FINAL-58d) + auto-bump
   * version + INSERT skills + dispatch wizard-complete.
   */
  async publishSkillWizard(executionId: string): Promise<{
    success: boolean;
    entity_type: 'skill';
    entity_id: string;
    slug: string;
    skill_md_path?: string;
    redirect_url: string;
  }> {
    return this.request(`/v1/wizards/executions/${executionId}/publish-skill`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async findActiveWizard(filters: { wizard_slug?: string; channel?: string; channel_user_id?: string }): Promise<{ wizard_execution: WizardExecution | null }> {
    const qs = new URLSearchParams();
    if (filters.wizard_slug) qs.set('wizard_slug', filters.wizard_slug);
    if (filters.channel) qs.set('channel', filters.channel);
    if (filters.channel_user_id) qs.set('channel_user_id', filters.channel_user_id);
    const q = qs.toString();
    return this.request<{ wizard_execution: WizardExecution | null }>(`/v1/wizards/active${q ? '?' + q : ''}`);
  }

  /**
   * F-FINAL-60 (2026-05-26): GET 1 wizard_execution específico pelo ID pra
   * suportar URL deeplink `?execution=<uuid>` nos 3 wizards conversacionais.
   *
   * Quando URL tem `?execution=<id>`, page carrega state existente em vez
   * de criar novo wizard. Permite bookmark / refresh / share / copiar ID
   * quando dá erro.
   *
   * Backend retorna shape paralelo ao start: { execution, wizard, opening_message }.
   * opening_message vem renderizado (placeholder {admin_name} substituído).
   * RLS via tenant_id: 404 quando execution não existe OU pertence a outro tenant.
   */
  async getWizardExecution(executionId: string): Promise<{
    execution: WizardExecution;
    wizard: { slug: string; name: string; artifact_type: string } | null;
    opening_message: string | null;
  }> {
    return this.request<{
      execution: WizardExecution;
      wizard: { slug: string; name: string; artifact_type: string } | null;
      opening_message: string | null;
    }>(`/v1/wizards/executions/${executionId}`);
  }

  // Agents
  async getAgents() {
    return this.request<Array<{ agent_id: string; name: string; role: string; domain: string }>>('/v1/agents');
  }

  // Agents (full — unified companion + agent listing)
  async listAgents(params?: { archetype?: string; scope?: string }): Promise<AgentListItem[]> {
    const qs = new URLSearchParams();
    if (params?.archetype) qs.set('archetype', params.archetype);
    if (params?.scope) qs.set('scope', params.scope);
    const q = qs.toString();
    const data = await this.request<AgentListItem[] | { agents: AgentListItem[] }>(
      `/v1/agents${q ? '?' + q : ''}`
    );
    return Array.isArray(data) ? data : (data.agents ?? []);
  }

  async getAgent(slugOrId: string, opts?: { skipMemories?: boolean }): Promise<AgentDetail> {
    // AGT-08: ?memories=skip — detalhe leve pro modal abrir rápido (memórias
    // de companion somam ~275KB e a query é o maior custo do GET); a aba
    // Memory carrega o detalhe completo lazy.
    const qs = opts?.skipMemories ? '?memories=skip' : '';
    return this.request<AgentDetail>(`/v1/agents/${slugOrId}${qs}`);
  }

  async createAgent(payload: CreateAgentPayload): Promise<AgentDetail> {
    return this.request<AgentDetail>('/v1/agents', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * PATCH /v1/agents/:slug. Além dos campos de CreateAgentPayload, o contrato
   * pós-fix do backend aceita `status`, `avatar_url`, `model_policy` (top-level,
   * AGT-02) e `heartbeat` — backend ANTIGO descarta/recusa os campos novos
   * (422 "Nenhum campo atualizável"); callers degradam com toast.
   */
  async updateAgent(slugOrId: string, payload: UpdateAgentPayload): Promise<AgentDetail> {
    return this.request<AgentDetail>(`/v1/agents/${slugOrId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  // AGT-12: memórias da aba Memory — endpoint dedicado e leve (só
  // companion_memory, 300 mais recentes), sem re-buscar o detalhe inteiro.
  async getAgentMemories(slugOrId: string): Promise<{ topic_files: NonNullable<NonNullable<AgentDetail['memory']>['topic_files']> }> {
    return this.request(`/v1/agents/${slugOrId}/memories`);
  }

  // AGT-12: estado do heartbeat do companion (config + último beat + registros).
  async getAgentHeartbeat(slugOrId: string): Promise<AgentHeartbeatState> {
    return this.request(`/v1/agents/${slugOrId}/heartbeat`);
  }

  async listAgentExecutions(slugOrId: string): Promise<AgentExecution[]> {
    const data = await this.request<AgentExecution[] | { executions: AgentExecution[] }>(
      `/v1/agents/${slugOrId}/executions`
    );
    return Array.isArray(data) ? data : (data.executions ?? []);
  }

  // --- Activity feed (Tela /mc/activity) — RPC unificado das 3 tabelas ---
  async listActivity(params?: {
    limit?: number;
    cursor_ts?: string;
    cursor_id?: string;
    kind?: 'all' | 'agent' | 'skill' | 'ephemeral' | 'handler' | 'companion';
    status?: 'all' | 'running' | 'completed' | 'failed' | 'timeout' | 'cancelled' | 'partial';
    actor_slug?: string;
    since?: string;
    /** Opcao B: default true (so rows root). false expande LLM calls + tool calls. */
    only_root?: boolean;
    /** Drill-down: lista todos os children de um root_trace_id especifico. */
    root_trace_id?: string;
  }): Promise<import('./activity').ActivityListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.cursor_ts) qs.set('cursor_ts', params.cursor_ts);
    if (params?.cursor_id) qs.set('cursor_id', params.cursor_id);
    if (params?.kind && params.kind !== 'all') qs.set('kind', params.kind);
    if (params?.status && params.status !== 'all') qs.set('status', params.status);
    if (params?.actor_slug) qs.set('actor_slug', params.actor_slug);
    if (params?.since) qs.set('since', params.since);
    if (params?.only_root === false) qs.set('only_root', 'false');
    if (params?.root_trace_id) qs.set('root_trace_id', params.root_trace_id);
    const q = qs.toString();
    return this.request<import('./activity').ActivityListResponse>(
      `/v1/activity${q ? '?' + q : ''}`
    );
  }

  async getActivityDetail(
    kind: 'agent' | 'skill' | 'ephemeral' | 'handler' | 'companion',
    id: string,
  ): Promise<{
    kind: 'agent' | 'skill' | 'ephemeral' | 'handler' | 'companion';
    item: import('./activity').ActivityDetail;
  }> {
    return this.request<{
      kind: 'agent' | 'skill' | 'ephemeral' | 'handler' | 'companion';
      item: import('./activity').ActivityDetail;
    }>(`/v1/activity/${kind}/${id}`);
  }

  // Skills & Agents Engine — lifecycle actions
  async installAgent(slugOrId: string): Promise<AgentDetail> {
    return this.request<AgentDetail>(`/v1/agents/${slugOrId}/install`, {
      method: 'POST',
    });
  }

  async forkAgent(slugOrId: string, newSlug: string): Promise<AgentDetail> {
    return this.request<AgentDetail>(`/v1/agents/${slugOrId}/fork`, {
      method: 'POST',
      body: JSON.stringify({ new_slug: newSlug }),
    });
  }

  async promoteAgent(
    slugOrId: string,
    targetScope: 'tenant' | 'template' | 'global',
    force: boolean = false
  ): Promise<PromoteResponse> {
    return this.request<PromoteResponse>(
      `/v1/agents/${slugOrId}/promote`,
      {
        method: 'POST',
        body: JSON.stringify({ target_scope: targetScope, force }),
      }
    );
  }

  // --- Skills ---
  async listSkills(filters?: SkillFilters): Promise<Skill[]> {
    const params = new URLSearchParams();
    if (filters?.scope) params.set('scope', filters.scope);
    if (filters?.maturity_level) params.set('maturity_level', filters.maturity_level);
    if (filters?.sizing) params.set('sizing', filters.sizing);
    const qs = params.toString();
    const data = await this.request<Skill[] | { skills: Skill[] }>(`/v1/skills${qs ? '?' + qs : ''}`);
    return Array.isArray(data) ? data : (data.skills ?? []);
  }

  async getSkill(idOrSlug: string): Promise<SkillDetail> {
    return this.request<SkillDetail>(`/v1/skills/${idOrSlug}`);
  }

  async createSkill(payload: CreateSkillPayload): Promise<SkillDetail> {
    return this.request<SkillDetail>('/v1/skills', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSkill(idOrSlug: string, payload: Partial<CreateSkillPayload>): Promise<SkillDetail> {
    return this.request<SkillDetail>(`/v1/skills/${idOrSlug}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async listSkillExecutions(idOrSlug: string): Promise<SkillExecution[]> {
    const data = await this.request<SkillExecution[] | { executions: SkillExecution[] }>(
      `/v1/skills/${idOrSlug}/executions`
    );
    return Array.isArray(data) ? data : (data.executions ?? []);
  }


  // ─── SQUADS ────────────────────────────────────────────────────────────────
  //
  // O quarto ativo. Endpoints em `/v1/squads` (backend PR #1389). Leitura
  // apenas: criar Squad é via Companion (`create_squad`), como manda o fluxo —
  // a tela não abre um segundo caminho de criação.

  async listSquads(filters?: SquadFilters): Promise<SquadListPage> {
    const params = new URLSearchParams();
    if (filters?.scope) params.set('scope', filters.scope);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.orchestrator) params.set('orchestrator', filters.orchestrator);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.per_page) params.set('per_page', String(filters.per_page));
    const qs = params.toString();
    return this.request<SquadListPage>(`/v1/squads${qs ? '?' + qs : ''}`);
  }

  async getSquad(id: string): Promise<SquadDetail> {
    return this.request<SquadDetail>(`/v1/squads/${id}`);
  }

  /** Orquestradores que existem nas squads do tenant. Alimenta o filtro. */
  async listSquadOrchestrators(): Promise<SquadOrchestrator[]> {
    const r = await this.request<{ data: SquadOrchestrator[] }>('/v1/squads/orchestrators');
    return r.data ?? [];
  }

  async getSquadMetrics(id: string): Promise<SquadMetricsResponse> {
    return this.request<SquadMetricsResponse>(`/v1/squads/${id}/metrics`);
  }

  async listSquadVersions(id: string): Promise<SquadVersion[]> {
    const r = await this.request<{ data: SquadVersion[] }>(`/v1/squads/${id}/versions`);
    return r.data ?? [];
  }

  async listSquadRuns(id: string, limit = 20): Promise<SquadRun[]> {
    const r = await this.request<{ data: SquadRun[] }>(`/v1/squads/${id}/runs?limit=${limit}`);
    return r.data ?? [];
  }

  async getSquadRun(runId: string): Promise<SquadRunDetail> {
    return this.request<SquadRunDetail>(`/v1/squads/runs/${runId}`);
  }

  // Skill lifecycle actions (install / fork / promote)
  async installSkill(slug: string): Promise<{ skill_id: string; slug: string; installed_at: string }> {
    return this.request<{ skill_id: string; slug: string; installed_at: string }>(
      `/v1/skills/${slug}/install`,
      { method: 'POST' }
    );
  }

  async forkSkill(
    slug: string,
    payload: { new_slug: string; target_scope: 'agent' | 'tenant'; agent_id?: string }
  ): Promise<{ skill_id: string; slug: string; scope: SkillScope }> {
    return this.request<{ skill_id: string; slug: string; scope: SkillScope }>(
      `/v1/skills/${slug}/fork`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  }

  async promoteSkill(
    slug: string,
    payload: { target_scope: 'tenant' | 'global'; force?: boolean }
  ): Promise<PromoteResponse> {
    return this.request<PromoteResponse>(
      `/v1/skills/${slug}/promote`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  }

  // --- F-FINAL-43 Onda 5 (UI v2) — version hint via changelog-template ---

  /**
   * Consulta o changelog-template do backend (Onda 4 sub-task 4.6) pra estimar
   * a próxima versão da skill no wizard.
   *
   * Comportamento:
   *  - Sem versões prévias no DB → 404 → frontend trata como "primeira publish v0.1".
   *  - Com versões prévias → retorna template + diff + is_first_publish=false.
   *
   * Frontend usa `from_version`/`to_version`/`diff` pra decidir o label
   * "v0.X (próximo MINOR)" vs "vY.0 (próximo MAJOR — contrato mudou)".
   */
  async getSkillChangelogTemplate(
    slug: string,
    opts?: { fromVersion?: string; toVersion?: string },
  ): Promise<{
    slug: string;
    from_version: string | null;
    to_version: string;
    template_markdown: string;
    is_first_publish: boolean;
    diff?: {
      input_changes?: Array<{ kind: 'add' | 'remove' | 'modify'; name: string }>;
      output_changes?: Array<{ kind: 'add' | 'remove' | 'modify'; name: string }>;
      is_major?: boolean;
    };
  }> {
    const params = new URLSearchParams();
    if (opts?.fromVersion) params.set('fromVersion', opts.fromVersion);
    if (opts?.toVersion) params.set('toVersion', opts.toVersion);
    const qs = params.toString();
    return this.request(
      `/v1/skills/${encodeURIComponent(slug)}/changelog-template${qs ? '?' + qs : ''}`,
    );
  }

  // --- Onda v6 plano (Skills Cycle Complete) — endpoints novos ---

  /** Detalhes completos de uma execução de skill (Input + Steps + Output).
   * Usado pelo SkillExecutionDetailModal na aba Execuções da SkillDetailPage. */
  async getSkillExecutionDetail(executionId: string): Promise<{
    execution: {
      id: string;
      skill_id: string;
      tenant_id: string;
      invoker_type: string;
      caller_channel?: string | null;
      session_id?: string | null;
      started_at: string;
      ended_at?: string | null;
      status: 'running' | 'ok' | 'failed' | 'partial' | 'canceled';
      input?: Record<string, unknown> | null;
      output?: SkillExecutionOutput | null;
      error?: Record<string, unknown> | null;
      duration_ms?: number | null;
      tokens_input?: number | null;
      tokens_output?: number | null;
      trigger_summary?: string | null;
      [k: string]: unknown;
    };
    steps: Array<{
      id: string;
      step_number: number;
      step_name: string;
      status: 'running' | 'ok' | 'failed';
      started_at: string;
      ended_at?: string | null;
      tokens_used?: number | null;
      input?: Record<string, unknown> | null;
      output?: SkillExecutionOutput | null;
      error?: Record<string, unknown> | null;
    }>;
    skill: { slug: string; name: string; version: string } | null;
  }> {
    return this.request(`/v1/skill-executions/${executionId}`);
  }

  // PR7-fix-B (2026-05-02): dispatchSkillBuilder REMOVIDA. /v1/skill-builder/chat
  // foi deletado no backend. Skill conversational agora usa wizardTurn(executionId,
  // user_message) → /v1/wizards/executions/:id/turn → @wizard-runner state-backed.

  /**
   * F-FINAL-66/68/69 (2026-05-26) — Upload canonical via POST /v1/uploads.
   *
   * Single endpoint pra TODOS uploads do frontend (chat attachments, file pickers
   * de skill inputs, etc). Backend (raios PR #634) recebe multipart/form-data com
   * field `file` (e opcional `filename`), persiste em bucket `raios-tenant-assets/
   * uploads/` e devolve signed URL pra preview imediato.
   *
   * Substitui o caminho legacy de `uploadWizardFile` (base64-em-payload). Esta
   * função é A canônica — toda nova UI deve chamar essa.
   */
  async uploadFile(
    file: File,
    opts?: {
      filename?: string;
      /**
       * Reforma UX §7.5 — `purpose: 'asset'` pede signed URL de longa duração
       * (~10 anos) pro backend, pra assets persistidos em colunas (logo de
       * negócio, avatar de agente). Sem isso o TTL default de 1h matava a URL
       * salva (logo/avatar quebrava após 1h). Backend pré-restart ignora o
       * field extra de multipart (response igual, TTL default) — degrade
       * graceful, `signed_url_ttl_seconds` reflete o TTL efetivo.
       */
      purpose?: 'asset';
    },
  ): Promise<UploadFileResponse> {
    const form = new FormData();
    form.append('file', file);
    if (opts?.filename) form.append('filename', opts.filename);
    if (opts?.purpose) form.append('purpose', opts.purpose);

    const headers: Record<string, string> = {
      // Não setar Content-Type: o browser monta com boundary correto.
      Accept: 'application/json',
    };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/v1/uploads`, {
      method: 'POST',
      headers,
      body: form,
    });

    const rawText = await res.text();
    let json: ApiResponse<UploadFileResponse>;
    try {
      json = rawText ? JSON.parse(rawText) : ({} as ApiResponse<UploadFileResponse>);
    } catch {
      const preview = rawText.slice(0, 300).replace(/\s+/g, ' ');
      throw new Error(`Upload: resposta inválida do servidor (HTTP ${res.status}): ${preview}`);
    }
    if (!res.ok || !json.success) {
      if (res.status === 401) {
        this.logout();
        window.location.href = '/login';
        throw new Error('Sessao expirada. Redirecionando para login...');
      }
      if (res.status === 413) {
        throw new Error(json.error?.message ?? 'Arquivo muito grande (máximo 50 MB).');
      }
      throw new Error(json.error?.message ?? `Erro ${res.status} ao subir arquivo.`);
    }
    return json.data;
  }

  /** Storage uploads pra arquivos do wizard (references, scripts, brand-manual, etc).
   * @deprecated F-FINAL-69 — chat attach migrou pra `api.uploadFile()` (POST
   * /v1/uploads). Esta função fica só pra `SkillManualWizardPage` que ainda
   * persiste references/scripts via `skill_drafts.files_uploaded[]` (publish-skill
   * canonical pipeline distinto do chat). Quando migrar references manual pro
   * /v1/uploads (débito futuro), remover. */
  async uploadWizardFile(file: File): Promise<{
    name: string;
    size: number;
    type: string;
    content_base64: string;
  }> {
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]!);
    }
    const content_base64 = btoa(binary);
    return {
      name: file.name,
      size: file.size,
      type: file.type || 'application/octet-stream',
      content_base64,
    };
  }

  /** Onda G.1 — dispara skill via POST /skills/:slug/execute (cria task event_type='skill.standalone') */
  async executeSkill(
    slug: string,
    args: Record<string, unknown> = {}
  ): Promise<{ task_id: string; skill_id?: string }> {
    return this.request(`/v1/skills/${slug}/execute`, {
      method: 'POST',
      body: JSON.stringify({ args }),
    });
  }

  /** Onda D.4 — dispara skill-eval-runner pra rodar evals_contract.cases */
  async evaluateSkill(
    slug: string
  ): Promise<{
    skill_id: string;
    skill_slug: string;
    total_cases: number;
    passed: number;
    failed: number;
    cases: Array<{ case_id: string; passed: boolean; duration_ms: number; missing?: string[]; error?: string }>;
  }> {
    return this.request(`/v1/skills/${slug}/evaluate`, { method: 'POST' });
  }

  /** Onda H.1 — cria nova versao da skill (auto-incrementa minor; aceita override) */
  async createSkillVersion(
    slug: string,
    payload?: { version?: string }
  ): Promise<{ id: string; slug: string; version: string; status: string; from_version: string }> {
    return this.request(`/v1/skills/${slug}/versions`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    });
  }

  /** Onda H.2 — deleta skill (admin pra global, owner pra tenant). Trigger 126 audit log captura. */
  async deleteSkill(slug: string): Promise<{ deleted: true; slug: string; id: string }> {
    return this.request(`/v1/skills/${slug}`, { method: 'DELETE' });
  }

  // --- Skill Drafts (Onda E backend completion) ---

  async createSkillDraft(payload: {
    mode: 'manual' | 'conversational';
    payload?: Record<string, unknown>;
    files_uploaded?: Array<Record<string, unknown>>;
    current_step?: string;
    chat_history?: unknown[];
  }): Promise<{ id: string; mode: string; version: number; created_at: string; updated_at: string }> {
    return this.request('/v1/skill-drafts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async listSkillDrafts(includePublished = false): Promise<{
    drafts: Array<{
      id: string;
      mode: string;
      payload: Record<string, unknown>;
      current_step: string | null;
      version: number;
      created_at: string;
      updated_at: string;
      published_at: string | null;
      published_skill_id: string | null;
    }>;
  }> {
    const qs = includePublished ? '?include_published=true' : '';
    return this.request(`/v1/skill-drafts${qs}`);
  }

  async getSkillDraft(id: string): Promise<{
    id: string;
    mode: string;
    payload: Record<string, unknown>;
    files_uploaded: Array<Record<string, unknown>>;
    current_step: string | null;
    chat_history: unknown[] | null;
    version: number;
    created_at: string;
    updated_at: string;
    published_at: string | null;
    published_skill_id: string | null;
  }> {
    return this.request(`/v1/skill-drafts/${id}`);
  }

  /** PATCH com optimistic locking — passa version atual; backend retorna 409 se outra aba editou */
  async patchSkillDraft(
    id: string,
    payload: {
      version: number;
      payload?: Record<string, unknown>;
      files_uploaded?: Array<Record<string, unknown>>;
      current_step?: string;
      chat_history?: unknown[];
    }
  ): Promise<{ id: string; mode: string; payload: Record<string, unknown>; version: number; updated_at: string }> {
    return this.request(`/v1/skill-drafts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteSkillDraft(id: string): Promise<{ deleted: true; id: string }> {
    return this.request(`/v1/skill-drafts/${id}`, { method: 'DELETE' });
  }

  async publishSkillDraft(
    id: string
  ): Promise<{ skill: { id: string; slug: string; version: string }; draft_id: string }> {
    return this.request(`/v1/skill-drafts/${id}/publish`, { method: 'POST' });
  }

  // --- Admin Promotions ---
  async listAdminPromotions(filters?: {
    status?: PromotionStatus | '';
    type?: PromotionType | '';
  }): Promise<PromotionProposal[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.type) params.set('type', filters.type);
    const qs = params.toString();
    const data = await this.request<
      PromotionProposal[] | { promotions: PromotionProposal[] }
    >(`/v1/admin/promotions${qs ? '?' + qs : ''}`);
    return Array.isArray(data) ? data : (data.promotions ?? []);
  }

  async approvePromotion(id: string, reviewNotes?: string): Promise<PromotionProposal> {
    return this.request<PromotionProposal>(`/v1/admin/promotions/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ review_notes: reviewNotes ?? null }),
    });
  }

  async rejectPromotion(id: string, reviewNotes: string): Promise<PromotionProposal> {
    return this.request<PromotionProposal>(`/v1/admin/promotions/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ review_notes: reviewNotes }),
    });
  }

  // --- Runtime Nodes (fleet monitoring) ---
  async listRuntimeNodes(): Promise<RuntimeNodesResponse> {
    const data = await this.request<
      RuntimeNode[] | RuntimeNodesResponse
    >(`/v1/admin/runtime-nodes`);
    if (Array.isArray(data)) {
      return { nodes: data };
    }
    return data;
  }

  // --- Companions (multi-bot per tenant — Phase 3) ---
  async listCompanions(): Promise<CompanionListItem[]> {
    const data = await this.request<
      CompanionListItem[] | { companions: CompanionListItem[] }
    >('/v1/companions');
    return Array.isArray(data) ? data : (data.companions ?? []);
  }

  async getCompanion(id: string): Promise<CompanionDetail> {
    return this.request<CompanionDetail>(`/v1/companions/${id}`);
  }

  async createCompanion(payload: CreateCompanionPayload): Promise<CompanionListItem> {
    return this.request<CompanionListItem>('/v1/companions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateCompanion(id: string, payload: UpdateCompanionPayload): Promise<CompanionDetail> {
    return this.request<CompanionDetail>(`/v1/companions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteCompanion(id: string): Promise<void> {
    return this.request<void>(`/v1/companions/${id}`, {
      method: 'DELETE',
    });
  }

  // --- MCP tokens (F4 — MCP Server do RAIOS Core) ---
  async listMcpTokens(): Promise<McpTokenItem[]> {
    const data = await this.request<{ tokens: McpTokenItem[] }>('/v1/mcp-tokens');
    return data.tokens ?? [];
  }

  async createMcpToken(payload: { label: string; client_type?: string }): Promise<McpTokenCreated> {
    return this.request<McpTokenCreated>('/v1/mcp-tokens', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /** Reinicia o backend (admin-only). pm2 sobe o processo de novo em ~10s. */
  async restartSystem(): Promise<{ restarting: boolean; message: string }> {
    return this.request<{ restarting: boolean; message: string }>('/v1/admin/system/restart', {
      method: 'POST',
    });
  }

  /** Busca o token em claro pro botão COPIAR (UI nunca renderiza; só clipboard). */
  async getMcpTokenSecret(id: string): Promise<string> {
    const data = await this.request<{ token: string }>(`/v1/mcp-tokens/${id}/secret`);
    return data.token;
  }

  async revokeMcpToken(id: string): Promise<void> {
    return this.request<void>(`/v1/mcp-tokens/${id}`, { method: 'DELETE' });
  }

  // ─── Conectores (DEM-2120 F2 — Marketplace UI, contratos F1/DEM-2119) ──────
  // Catálogo GLOBAL de conectores. Backend suporta ?category= e ?q= (ILIKE sobre
  // description via índice GIN trigram); a busca full-text nome+descrição+categoria
  // é feita client-side sobre este payload (catálogo pequeno — 8 seeds).
  async listConnectorCatalog(): Promise<ConnectorCatalogItem[]> {
    const data = await this.request<{ connectors: ConnectorCatalogItem[]; redirect_uri?: string }>(
      '/v1/connectors/catalog',
    );
    return data.connectors ?? [];
  }

  // DEM-2128: mesma chamada do catálogo, mas devolve TAMBÉM a redirect_uri exata
  // do callback central do Core (por instalação) — o modal a exibe pro usuário
  // copiar pro console do app dele (Meta/Google/TikTok/LinkedIn). Fonte única: o
  // backend computa a URI de connectorRedirectUri() e a devolve no catálogo.
  async getConnectorCatalog(): Promise<{
    connectors: ConnectorCatalogItem[];
    redirect_uri: string;
  }> {
    const data = await this.request<{ connectors: ConnectorCatalogItem[]; redirect_uri?: string }>(
      '/v1/connectors/catalog',
    );
    return { connectors: data.connectors ?? [], redirect_uri: data.redirect_uri ?? '' };
  }

  // Conexões do tenant. ?business_id filtra por vínculo; revogadas já são
  // excluídas por default (só voltam com include_revoked=true).
  async listConnectorConnections(params?: {
    business_id?: string;
    include_revoked?: boolean;
  }): Promise<ConnectorConnection[]> {
    const qs = new URLSearchParams();
    if (params?.business_id) qs.set('business_id', params.business_id);
    if (params?.include_revoked) qs.set('include_revoked', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const data = await this.request<{ connections: ConnectorConnection[] }>(
      `/v1/connectors/connections${suffix}`,
    );
    return data.connections ?? [];
  }

  /** Canais de COMUNICAÇÃO conectados pro canal de saída de loops (bots Telegram dos companions) — GET /v1/loops/channels. */
  async listLoopChannels(): Promise<LoopOutputChannel[]> {
    const data = await this.request<LoopOutputChannel[] | { channels: LoopOutputChannel[] }>('/v1/loops/channels');
    return Array.isArray(data) ? data : (data.channels ?? []);
  }

  // Cria (ou re-cria, no fluxo de "reconectar") uma conexão. A credencial VAI
  // SEMPRE pro vault: o frontend manda `secret_value` (a chave colada / config
  // do MCP server) e o backend cifra via encrypt_tenant_secret + persiste em
  // tenant_secrets, gravando só o secret_id na conexão. O valor NUNCA volta.
  async createConnectorConnection(payload: {
    connector_slug: string;
    display_name: string;
    auth_mode: ConnectorAuthMode;
    business_id?: string | null;
    secret_value?: string;
    external_account?: Record<string, unknown>;
  }): Promise<ConnectorConnection> {
    const data = await this.request<{ connection: ConnectorConnection }>(
      '/v1/connectors/connections',
      { method: 'POST', body: JSON.stringify(payload) },
    );
    return data.connection;
  }

  // Revoga: status='revoked' + soft-delete do secret no vault (F1/DEM-2119).
  async revokeConnectorConnection(id: string): Promise<{ id: string; status: string; secret_soft_deleted: boolean }> {
    return this.request<{ id: string; status: string; secret_soft_deleted: boolean }>(
      `/v1/connectors/connections/${id}`,
      { method: 'DELETE' },
    );
  }

  // ─── DEM-2124 (F3) — OAuth2 central ────────────────────────────────────────
  // Inicia o fluxo OAuth: o backend valida o catálogo, monta a authorize_url
  // (state assinado HMAC) e a devolve. O front redireciona o usuário pra lá; o
  // callback do Core grava o token no vault e volta pra /settings/connectors.
  // reconnect_connection_id: reconecta uma conexão existente (CTA de expirado).
  // byo_client_id/secret: LinkedIn (app próprio do usuário, guardado no vault).
  async startConnectorOAuth(params: {
    connector_slug: string;
    business_id?: string | null;
    reconnect_connection_id?: string | null;
    byo_client_id?: string | null;
    byo_client_secret?: string | null;
    /** DEM-2124: 'mcp' dispara o handshake OAuth padrão MCP (discovery + DCR + PKCE) no backend. */
    auth_mode?: string | null;
  }): Promise<{ authorize_url: string; state: string }> {
    // DEM-2128: POST (não GET). No modelo 100% BYO o client_secret do app do
    // usuário vai no CORPO — nunca em query string / URL / histórico do browser.
    return this.request<{ authorize_url: string; state: string }>(
      '/v1/connectors/oauth/authorize',
      { method: 'POST', body: JSON.stringify(params) },
    );
  }

  // ─── DEM-2124 (F3 · AC4) — X modo MCP: registrar conexão com budget ────────
  async connectXMcp(payload: {
    business_id?: string | null;
    display_name?: string;
    budget_usd: number;
    server?: string;
  }): Promise<{ connection_id: string; budget: Record<string, unknown> }> {
    return this.request<{ connection_id: string; budget: Record<string, unknown> }>(
      '/v1/connectors/x-mcp/connect',
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  // F8 (UI) — cria ativo a partir de escopo livre (o "Criador"). Fire-and-forget
  // no backend: o ativo aparece na listagem quando pronto.
  async createAsset(
    type: 'skill' | 'agent' | 'pipeline',
    scope: string,
    archetype?: string,
  ): Promise<{ accepted: boolean; type: string; message: string }> {
    return this.request('/v1/assets/create', {
      method: 'POST',
      body: JSON.stringify({ type, scope, ...(archetype ? { archetype } : {}) }),
    });
  }

  // ─── Loops (DEM-2129 · Loops C) — consome a API Loops A (/v1/loops). ────────
  async listLoops(): Promise<Loop[]> {
    const data = await this.request<Loop[] | { loops: Loop[] }>('/v1/loops');
    return Array.isArray(data) ? data : (data.loops ?? []);
  }

  async getLoop(id: string, params?: { limit?: number; offset?: number }): Promise<LoopDetail> {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<LoopDetail>(`/v1/loops/${id}${suffix}`);
  }

  async createLoop(payload: CreateLoopPayload): Promise<Loop> {
    return this.request<Loop>('/v1/loops', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateLoop(id: string, payload: UpdateLoopPayload): Promise<Loop> {
    return this.request<Loop>(`/v1/loops/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async pauseLoop(id: string): Promise<Loop> {
    return this.request<Loop>(`/v1/loops/${id}/pause`, { method: 'POST' });
  }

  async resumeLoop(id: string): Promise<Loop> {
    return this.request<Loop>(`/v1/loops/${id}/resume`, { method: 'POST' });
  }

  async deleteLoop(id: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/v1/loops/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * MUST_FIX #1 — preview das próximas ocorrências. Tenta o endpoint backend
   * POST /v1/loops/preview (follow-up de Loops A); se retornar 404/501 (endpoint
   * ausente na API consumida), lança para o caller degradar pro cálculo
   * client-side via lib rrule (computeOccurrences de loops-rrule.ts).
   */
  async previewLoopOccurrences(payload: PreviewLoopPayload): Promise<{ occurrences: string[] }> {
    return this.request<{ occurrences: string[] }>('/v1/loops/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export interface McpTokenItem {
  id: string;
  label: string;
  client_type: string;
  token_prefix: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface McpTokenCreated {
  id: string;
  label: string;
  client_type: string;
  token_prefix: string;
  token_plaintext: string;
}

// ─── Conectores (DEM-2120 F2) — espelham o schema F1 (connector_catalog /
//     connector_connections, migration 20260708100000_dem2119). ──────────────
export type ConnectorAuthMode = 'api_key' | 'oauth2' | 'mcp';
export type ConnectorCategory =
  | 'social_media'
  | 'storage'
  | 'communication'
  | 'crm'
  | 'ads'
  | 'analytics'
  | 'payments'
  | 'marketplace'
  | 'other';
export type ConnectorCatalogStatus = 'available' | 'beta' | 'coming_soon' | 'deprecated';
export type ConnectorConnectionStatus = 'connected' | 'expired' | 'error' | 'revoked';

export interface ConnectorCatalogItem {
  slug: string;
  name: string;
  description: string | null;
  category: ConnectorCategory;
  logo_url: string | null;
  /** Subset de {'api_key','oauth2','mcp'} — modos de autenticação suportados. */
  supported_modes: ConnectorAuthMode[];
  capabilities: { publish?: boolean; read?: boolean; metrics?: boolean } & Record<string, unknown>;
  /**
   * DEM-2128: `byo_app` = todos os conectores oauth2 usam o app do usuário
   * (client_id/secret colados no modal, cifrados no vault). `setup_guide_md` =
   * passo-a-passo (markdown) de como criar o app na plataforma, exibido in-modal.
   */
  oauth_config: {
    byo_app?: boolean;
    /**
     * Guia único (retrocompat). Quando o conector suporta múltiplos modos, o guia
     * pertinente a cada modo mora em `setup_guides[<mode>]`; `setup_guide_md`
     * permanece como fallback pros conectores single-mode.
     */
    setup_guide_md?: string;
    /**
     * DEM-2153 — guia por MODO de conexão. Chave = ConnectorAuthMode
     * ('api_key' | 'oauth2' | 'mcp'). O modal exibe o guia do modo VISÍVEL/selecionado,
     * caindo em `setup_guide_md` quando o modo não tem entrada aqui. Contrato de CLASSE:
     * todo conector api_key nasce com `setup_guides.api_key` (enforced por gate no CI).
     */
    setup_guides?: Partial<Record<ConnectorAuthMode, string>>;
    docs_url?: string;
    /** Nomes oficiais dos campos de credencial na plataforma (Meta "App ID", TikTok "Client Key", "System User Access Token"...). */
    credential_labels?: { client_id?: string; client_secret?: string; api_key?: string };
  } & Record<string, unknown>;
  mcp_config: { server?: string; transport?: string; tools?: string[] } & Record<string, unknown>;
  status: ConnectorCatalogStatus;
  docs_url: string | null;
}

/** Canal de saída de loop = canal de COMUNICAÇÃO conectado (bot Telegram de companion). */
export interface LoopOutputChannel {
  id: string;
  kind: 'companion_telegram';
  provider: 'telegram';
  label: string;
  detail: string | null;
  status: 'connected';
}

export interface ConnectorConnection {
  id: string;
  connector_slug: string;
  display_name: string;
  auth_mode: ConnectorAuthMode;
  secret_id: string | null;
  /** NULL = conexão do tenant (tenant-wide); preenchido = atrelada a um business. */
  business_id: string | null;
  external_account: Record<string, unknown>;
  status: ConnectorConnectionStatus;
  status_detail?: string | null;
  last_verified_at?: string | null;
  created_at: string;
  updated_at?: string;
}

// --- Agents (Skills & Agents Engine) ---

export type AgentArchetype = 'companion' | 'domain_worker' | 'system' | 'ephemeral';
export type AgentScope = 'global' | 'tenant' | 'agent';
export type AgentStatus = 'draft' | 'active' | 'paused' | 'archived';

export interface AgentListItem {
  id: string;
  tenant_id: string | null;
  archetype: AgentArchetype;
  slug: string;
  name: string;
  role?: string | null;
  domain?: string | null;
  scope: AgentScope;
  layers?: string[] | null;
  status: AgentStatus;
  /** Reforma UX §7.5 — avatar do agente (agents.avatar_url; companion: identity.avatar_url). */
  avatar_url?: string | null;
  created_at: string;
  updated_at: string;
  /** Unified contract (Phase 2 T-2.1) — canonical identifiers from GET /v1/agents
   * (companion_profiles vs agents union). Used for dedup when both sides list
   * the same logical entity (e.g. Alfred cp mirrored as agents row). */
  entity_type?: 'agent' | 'companion_profile';
  entity_id?: string;
  /** AGT-04 — model_policy direto da listagem (companion: identity; agent:
   * coluna). Presente (mesmo que {}) = card NÃO precisa do GET de detalhe
   * pra preencher provedor/modelo (mata o N+1 da tela de agentes).
   * `undefined` = backend antigo → card mantém o fetch de fallback. */
  model_policy?: Record<string, unknown> | null;
}

export interface AgentDetail extends AgentListItem {
  engine?: string | null;
  lifecycle?: string | null;
  level?: string | null;
  autonomy?: number | null;
  version?: string | null;
  parent_agent_id?: string | null;
  /** Markdown content read from disk (identity.md). Null when not available. */
  identity_md?: string | null;
  soul_md?: string | null;
  user_md?: string | null;
  memory?: {
    topic_files?: Array<{
      path: string;
      title?: string;
      type?: string;
      importance?: number | null;
      preview?: string;
      content?: string;
      created_at?: string;
      updated_at?: string;
    }>;
  } | null;
  config?: {
    model_policy?: Record<string, unknown> | string | null;
    heartbeat_enabled?: boolean;
    autonomy_tiers?: Record<string, unknown> | string[];
    allowed_skills?: string[];
    allowed_tools?: string[];
  } | null;
  identity_md_path?: string | null;
  soul_md_path?: string | null;
  executions_count?: number | null;
}

// --- Companions (Phase 3: multi-bot per tenant) ---

export interface CompanionListItem {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  is_default: boolean;
  telegram_bot_username: string | null;
  telegram_bot_token_set: boolean;
  /** Reforma UX §7.5/§7.7 — avatar + sessões ativas por canal. */
  avatar_url?: string | null;
  channels?: Array<{ channel: string; active_sessions: number }>;
  created_at: string;
  updated_at: string;
}

export interface CompanionDetail extends CompanionListItem {
  tenant_id: string;
  user_id: string;
  identity: Record<string, unknown>;
  /** Raw token only returned on GET /v1/companions/:id. */
  telegram_bot_token: string | null;
}

export interface CreateCompanionPayload {
  name: string;
  identity?: Record<string, unknown>;
  is_default?: boolean;
  telegram_bot_token?: string;
}

export type UpdateCompanionPayload = Partial<{
  name: string;
  identity: Record<string, unknown>;
  is_default: boolean;
  telegram_bot_token: string | null;
  is_active: boolean;
}>;

export interface CreateAgentPayload {
  archetype: AgentArchetype;
  slug: string;
  name: string;
  role?: string;
  domain?: string;
  soul_md?: string;
  user_md?: string;
  memory?: {
    topic_files?: string[];
  };
  config?: {
    model_policy?: string;
    heartbeat_enabled?: boolean;
    autonomy_tiers?: string[];
    allowed_skills?: string[];
  };
}

/**
 * Reforma UX §6.6 — payload do PATCH /v1/agents/:slug (contrato pós-fix).
 * Aditivo sobre CreateAgentPayload: status (toggle do card), avatar_url
 * (§7.5), model_policy top-level (AGT-02) e heartbeat (intervalo).
 */
export type UpdateAgentPayload = Partial<CreateAgentPayload> & {
  status?: string;
  avatar_url?: string;
  /** AGT-08: aba Identity — PATCH aceita identity_md (alias → identity_md_content, materializado no storage). */
  identity_md?: string;
  model_policy?: Record<string, unknown>;
  heartbeat?: {
    enabled?: boolean;
    interval_value?: number;
    interval_unit?: string;
  };
};

// AGT-12 — GET /v1/agents/:slug/heartbeat
export interface AgentHeartbeatState {
  config: { enabled: boolean; interval_value: number; interval_unit: string };
  last_beat_at: string | null;
  /** AGT-13 — checagens AGENDADAS pelo companion (wireframe v18 hblog). */
  followups: Array<{ due_at: string; created_at: string; instruction: string }>;
  beats: Array<{ at: string; status: string; notified: boolean; silent: boolean; reason: string | null }>;
  notifications: Array<{ at: string; delivered_at: string | null; message: string }>;
}

export interface AgentExecution {
  id: string;
  agent_id: string;
  started_at: string;
  ended_at?: string | null;
  /** AGT-06 — backend manda completed_at (ended_at é legado de tipos antigos). */
  completed_at?: string | null;
  status: 'running' | 'success' | 'error' | 'cancelled' | 'completed' | 'failed' | string;
  trigger?: string | null;
  trace_id?: string | null;
  duration_ms?: number | null;
  /** AGT-06 — métricas pro feed/ActivityDetailModal. */
  tokens_input?: number | null;
  tokens_output?: number | null;
  cost_usd?: number | null;
  model_used?: string | null;
  error?: string | null;
}

// --- Skills (Agents Engine) ---

export type SkillScope = 'global' | 'tenant' | 'agent';
export type SkillOwner = 'global' | 'tenant' | 'agent';
export type SkillSizing = 'MICRO' | 'CURTA' | 'MEDIA' | 'LONGA' | 'UNKNOWN';
export type SkillMaturityLevel =
  | 'draft'
  | 'experimental'
  | 'beta'
  | 'stable'
  | 'deprecated';

export interface SkillReference {
  name: string;
  content: string;
}

export interface SkillEvalCase {
  id: string;
  label: string;
  input: string;
  expected_behavior: string;
}

export interface Skill {
  id: string;
  tenant_id: string | null;
  agent_id?: string | null;
  slug: string;
  name: string;
  description: string;
  scope: SkillScope;
  owner: SkillOwner;
  version: string;
  sizing: SkillSizing;
  maturity_level: SkillMaturityLevel;
  total_executions: number;
  avg_duration_ms?: number | null;
  p95_duration_ms?: number | null;
  scoring_6d?: Record<string, number> | null;
  /** Correção UX v2 §5.6-C2 — métricas reais na LISTA (executors.length; taxa 0-1). */
  steps_count?: number | null;
  success_rate?: number | null;
  created_at: string;
  updated_at: string;
}

export interface SkillDetail extends Skill {
  skill_md_content?: string | null;
  references_bundle?: SkillReference[] | null;
  evals_contract?: SkillEvalCase[] | null;
}

export interface SkillFilters {
  scope?: SkillScope | '';
  maturity_level?: SkillMaturityLevel | '';
  sizing?: SkillSizing | '';
}

export interface CreateSkillPayload {
  slug: string;
  name: string;
  description: string;
  skill_md_content: string;
  references_bundle?: SkillReference[];
  evals_contract: SkillEvalCase[];
  scope: Exclude<SkillScope, 'global'>;
  /** Canonical backend field (skills.routes.ts §CreateSkillSchema). */
  owner_agent_id?: string;
  /** @deprecated use `owner_agent_id` — legacy alias kept for transition. */
  agent_id?: string;
}

export interface SkillExecution {
  id: string;
  skill_id: string;
  agent_id?: string | null;
  started_at: string;
  ended_at?: string | null;
  status: 'running' | 'success' | 'error' | 'cancelled';
  duration_ms?: number | null;
  trigger?: string | null;
  trace_id?: string | null;
}

// --- Admin Promotions (cron candidate / manual force) ---

export type PromotionType = 'skill' | 'agent';
export type PromotionStatus = 'pending' | 'approved' | 'rejected' | 'applied' | 'cancelled';
export type PromotionMode = 'cron_candidate' | 'manual_force';
export type PromotionScope = 'agent' | 'tenant' | 'global';

export interface PromotionProposal {
  id: string;
  type: PromotionType;
  slug: string;
  subject_id: string;
  source_scope: PromotionScope;
  target_scope: PromotionScope;
  mode: PromotionMode;
  status: PromotionStatus;
  proposed_by: string | null;
  proposed_by_email?: string | null;
  proposed_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_notes?: string | null;
  diff_snapshot?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

// --- Runtime Nodes (fleet monitoring) ---

export type RuntimeNodeStatus = 'online' | 'draining' | 'offline';

export interface RuntimeNode {
  id: string;
  hostname: string;
  tailscale_ip?: string | null;
  ram_gb: number;
  max_workers: number;
  status: RuntimeNodeStatus;
  last_heartbeat_at: string | null;
  tasks_in_progress?: number | null;
  llm_sessions_active?: number | null;
  memory_pressure_events_24h?: number | null;
  metadata?: Record<string, unknown> | null;
}

export interface RuntimeAccountSession {
  account_id: string;
  account_label?: string | null;
  active_sessions: number;
}

export interface RuntimeMemoryPressureEvent {
  id: string;
  node_id: string;
  hostname?: string | null;
  level: 'warning' | 'critical';
  message?: string | null;
  occurred_at: string;
}

export interface RuntimeNodesResponse {
  nodes: RuntimeNode[];
  llm_sessions_by_account?: RuntimeAccountSession[];
  memory_pressure_events?: RuntimeMemoryPressureEvent[];
}

// ─── SQUADS ──────────────────────────────────────────────────────────────────

export type SquadScope = 'tenant' | 'global';

export interface SquadFilters {
  scope?: SquadScope;
  status?: string;
  search?: string;
  /** Slug do orquestrador. Vive na versão corrente, resolvido pelo backend. */
  orchestrator?: string;
  page?: number;
  per_page?: number;
}

/** Orquestrador que de fato coordena alguma squad, com quantas. */
export interface SquadOrchestrator {
  slug: string;
  squads_count: number;
}

/** DOD-SQD-404 — agregado por squad, calculado pela view `v_squad_metrics`. */
export interface SquadMetrics {
  runs_total: number;
  runs_terminais: number;
  runs_aceitas: number;
  runs_first_pass: number;
  runs_escaladas: number;
  /** `null` quando não há run terminal — não há o que medir, e não "0%". */
  outcome_acceptance: number | null;
  first_pass_yield: number | null;
  cycle_time_p50_s: number | null;
  cycle_time_p95_s: number | null;
  handoffs_rejeitados: number;
  handoffs_resolvidos: number;
  handoff_rejection_rate: number | null;
  custo_total_usd: number | null;
  custo_por_outcome_aceito_usd: number | null;
  tokens_input: number | null;
  tokens_output: number | null;
  dispatch_retries: number | null;
  ultima_conclusao: string | null;
}

export interface SquadCostRow {
  squad_run_id: string;
  run_status: string;
  custo_run: number;
  custo_membros: number;
  delta_custo: number;
  custo_fecha: boolean;
  tokens_fecham: boolean;
}

export interface SquadMetricsResponse {
  /** `null` = a squad nunca executou. Distinto de métricas zeradas. */
  metrics: SquadMetrics | null;
  reconciliacao: {
    runs_avaliadas: number;
    runs_que_nao_fecham: number;
    amostra: SquadCostRow[];
  };
}

export interface Squad {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  /** Explícito no payload — a UI NÃO infere GLOBAL de tenant_id nulo. */
  scope: SquadScope;
  status: string;
  /**
   * Da VERSÃO corrente, não da squad — a tabela `squads` não tem estes campos e
   * não deveria ter: topologia e orquestrador são estrutura declarada no
   * SQUAD.md, e mudam de uma versão para a outra.
   */
  topology: string | null;
  orchestrator_slug: string | null;
  current_version: string | null;
  /**
   * `null` = O BACKEND NÃO CONSEGUIU CONTAR, e não zero. Zero afirmaria "esta
   * squad nunca executou"; a tela mostra "—" quando é null. Tratar null como 0
   * aqui reintroduz na UI exatamente a mentira que o backend evitou.
   */
  members_count: number | null;
  runs_count: number | null;
  /** Linhagem: instância criada a partir de uma GLOBAL. */
  parent_global_id: string | null;
  origin_version: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface SquadListPage {
  data: Squad[];
  total: number;
  page: number;
  per_page: number;
}

export interface SquadVersion {
  id: string;
  squad_id: string;
  version: string;
  content_hash: string | null;
  squad_md_path: string | null;
  topology: string | null;
  orchestrator_slug: string | null;
  changelog: string | null;
  created_at: string;
  /**
   * `squad_versions` NÃO tem coluna `status`. O estado da versão É este campo:
   * nulo = ainda não publicada. Um `status` aqui seria uma coluna inventada.
   */
  published_at: string | null;
}

/** Papel do roster, parseado do SQUAD.md — a mesma fonte que o runtime lê. */
export interface SquadRosterMember {
  role: string;
  agent_slug?: string | null;
  execution_role?: string | null;
  activation?: string | null;
  tools?: string[] | null;
  [k: string]: unknown;
}

export interface SquadDetail extends Squad {
  tenant_id: string | null;
  /**
   * Qual linha de `GET /:id/versions` é a corrente. `current_version` (herdado
   * de Squad) é a STRING da versão semântica nos dois endpoints — mesmo nome,
   * mesmo tipo, venha de onde vier.
   */
  current_version_id: string | null;
  roster: SquadRosterMember[];
  charter: string | null;
  [k: string]: unknown;
}

export interface SquadRun {
  id: string;
  status: string;
  status_reason: string | null;
  demand_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  iterations_used: number | null;
  tokens_input_total: number | null;
  tokens_output_total: number | null;
  cost_usd_total: number | null;
  active_members_peak: number | null;
  accepted_outcomes: unknown;
}

export interface SquadRunMember {
  id: string;
  role: string;
  agent_slug: string | null;
  execution_role: string | null;
  status: string;
  depth: number | null;
  activated_at: string | null;
  completed_at: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cost_usd: number | null;
  result_capsule: unknown;
}

export interface SquadHandoff {
  id: string;
  from_member_id: string | null;
  to_member_id: string | null;
  status: string;
  mode: string | null;
  objective: string | null;
  /** Só o handoff REJEITADO carrega motivo — daí o nome da coluna. */
  rejection_reason: string | null;
  proposed_at: string | null;
  accepted_at: string | null;
  submitted_at: string | null;
  resolved_at: string | null;
}

export interface SquadRunDetail extends SquadRun {
  squad_id: string;
  members: SquadRunMember[];
  handoffs: SquadHandoff[];
  [k: string]: unknown;
}

export const api = new ApiClient();

// DEM-2129 — re-export dos tipos de Loops pra callers que importam de '@/lib/api'.
export type {
  Loop,
  LoopDetail,
  LoopRun,
  LoopLastRun,
  LoopExecutionType,
  LoopStatus,
  LoopRunStatus,
  LoopCatchUpPolicy,
  CreateLoopPayload,
  UpdateLoopPayload,
  PreviewLoopPayload,
} from './types/loops';
